/**
 * Public Landing Page — trang chủ công khai.
 *
 * Style: giữ STRUCTURE của marketingarena.vn (top nav · hero · stats · overview ·
 * timeline · rules · awards · partners · contact · footer) NHƯNG dùng nhận diện
 * thương hiệu ICFGL UEL — sáng/trắng, navy + hot pink accent, sans-serif uppercase,
 * minimalist academic, không tech-dark.
 *
 * Nội dung lấy từ src/config/siteContent.ts.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Gavel,
  Login,
  HowToReg,
  OpenInNew,
  EmojiEvents,
  ArrowDownward,
  AccountBalance,
  Diversity3,
  Menu as MenuIcon,
  Close as CloseIcon,
  Article,
  Groups,
  Schedule,
  Mail,
  Visibility,
  CheckCircle,
  ExpandMore,
  Search as SearchIcon,
  Facebook,
  Public as PublicIcon,
  WorkspacePremium,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { siteContent } from '../config/siteContent'
import { brand, gradientBrand } from '../theme/brand'
import { useAppSelector } from '../hooks/redux'
import LanguageSwitcher from '../components/LanguageSwitcher'
import {
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
} from '../store/api/reportsApi'

// ─── Tokens — light academic palette (ICFGL UEL) ──────────────────────────
const BG_PRIMARY = '#FFFFFF'
const BG_SOFT = '#FAFBFD'
const INK = brand.navy // #144E8C
const ACCENT_PINK = '#D9356F'
const SOLID_BLUE = INK
const TEXT_BODY = '#2A3548'
const TEXT_DIM = '#5E6B7E'
const HAIRLINE = 'rgba(20,78,140,0.12)'
const BG_PINK = '#FFEBF2'
const BG_NAVY_DARK = '#071B33'
const TEMPLATE_PINK = '#F64183'
const TEMPLATE_CORAL = '#FF9797'
const BLUE_TINT_04 = 'rgba(20,78,140,0.04)'
const BLUE_TINT_08 = 'rgba(20,78,140,0.08)'
const BLUE_TINT_10 = 'rgba(20,78,140,0.10)'
const BLUE_TINT_14 = 'rgba(20,78,140,0.14)'
const BLUE_TINT_18 = 'rgba(20,78,140,0.18)'
const PINK_TINT_04 = 'rgba(217,53,111,0.04)'
const PINK_TINT_10 = 'rgba(217,53,111,0.10)'
const PINK_TINT_18 = 'rgba(217,53,111,0.18)'
const CARD_SHADOW = '0 14px 30px rgba(20,78,140,0.045)'
const CARD_SHADOW_HOVER = '0 18px 36px rgba(20,78,140,0.07)'
const AWARD_GOLD = '#C99A2E'
const AWARD_SILVER = '#8E98A8'
const AWARD_BRONZE = '#A56B45'
const templateNavySection =
  'radial-gradient(800px 500px at 90% 0%, rgba(246,65,131,0.25), transparent 60%), linear-gradient(160deg, #144E8C, #0C2E55)'
const templateHeroBackground =
  'radial-gradient(1100px 720px at 78% -8%, rgba(246,65,131,0.5), transparent 60%), radial-gradient(820px 620px at -6% 105%, rgba(255,151,151,0.32), transparent 55%), linear-gradient(155deg, rgba(20,78,140,0.98), rgba(7,27,51,0.96))'
const heroBackgroundImages = [
  '/1.jpg',
  '/498649275_714676411718356_1072487127192944158_n.jpg',
  '/2.jpg',
  '/499612947_714676348385029_1222981871341779425_n.jpg',
]
const RECAP_YOUTUBE_VIDEO_ID = 'QQDpNo-0qh8'
const RECAP_YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${RECAP_YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${RECAP_YOUTUBE_VIDEO_ID}&playsinline=1&controls=0&disablekb=1&fs=0&iv_load_policy=3&rel=0&modestbranding=1`
const PRELIMINARY_RESULTS_VISIBLE_AT = '2026-08-20T00:00:00+07:00'
const WATERMARK_OFFSET_1CM = 38
const WATERMARK_OFFSET_2CM = 76
const sectionTexture = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    opacity: 0.035,
    backgroundImage:
      'radial-gradient(rgba(20,78,140,0.45) 0.7px, transparent 0.7px)',
    backgroundSize: '18px 18px',
  },
}

const overviewAccents = [
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: BLUE_TINT_08, iconColor: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: PINK_TINT_10, iconColor: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: BLUE_TINT_08, iconColor: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: PINK_TINT_10, iconColor: ACCENT_PINK, accent: ACCENT_PINK },
]

const timelineAccents = [
  {
    surface: BG_PRIMARY,
    panel: BLUE_TINT_04,
    panelStrong: BLUE_TINT_08,
    border: HAIRLINE,
    marker: SOLID_BLUE,
    accent: SOLID_BLUE,
    shadow: 'rgba(20,78,140,0.16)',
  },
  {
    surface: BG_PRIMARY,
    panel: PINK_TINT_04,
    panelStrong: PINK_TINT_10,
    border: HAIRLINE,
    marker: ACCENT_PINK,
    accent: ACCENT_PINK,
    shadow: 'rgba(217,53,111,0.16)',
  },
  {
    surface: BG_PRIMARY,
    panel: BLUE_TINT_04,
    panelStrong: BLUE_TINT_08,
    border: HAIRLINE,
    marker: SOLID_BLUE,
    accent: SOLID_BLUE,
    shadow: 'rgba(20,78,140,0.16)',
  },
  {
    surface: BG_PRIMARY,
    panel: PINK_TINT_04,
    panelStrong: PINK_TINT_10,
    border: HAIRLINE,
    marker: ACCENT_PINK,
    accent: ACCENT_PINK,
    shadow: 'rgba(217,53,111,0.16)',
  },
]

const scheduleAccents = [
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
]

const awardAccents = [
  { surface: 'rgba(201,154,46,0.06)', border: 'rgba(201,154,46,0.2)', icon: AWARD_GOLD, shadow: 'rgba(201,154,46,0.14)' },
  { surface: 'rgba(142,152,168,0.07)', border: 'rgba(142,152,168,0.22)', icon: AWARD_SILVER, shadow: 'rgba(142,152,168,0.12)' },
  { surface: 'rgba(165,107,69,0.08)', border: 'rgba(165,107,69,0.22)', icon: AWARD_BRONZE, shadow: 'rgba(165,107,69,0.12)' },
]

function normalizeSearchText(value: string) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/đ/g, 'd')
    .trim()
}

function useOnceInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null)
  const [hasEntered, setHasEntered] = useState(false)

  useEffect(() => {
    if (hasEntered) return undefined
    const node = ref.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEntered(true)
          observer.disconnect()
        }
      },
      {
        threshold: 0.22,
        rootMargin: '0px 0px -12% 0px',
        ...options,
      },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasEntered, options])

  return [ref, hasEntered] as const
}

function useReplayInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null)
  const wasInViewRef = useRef(false)
  const [runKey, setRunKey] = useState(0)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !wasInViewRef.current) {
          wasInViewRef.current = true
          setRunKey((current) => current + 1)
        }

        if (!entry.isIntersecting) {
          wasInViewRef.current = false
        }
      },
      {
        threshold: 0.24,
        rootMargin: '0px 0px -10% 0px',
        ...options,
      },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [options])

  return [ref, runKey] as const
}

function formatAnimatedAmount(rawValue: string, currentValue: number) {
  const match = rawValue.match(/(\d[\d.,]*)/)
  if (!match || match.index === undefined) return rawValue

  const prefix = rawValue.slice(0, match.index)
  const suffix = rawValue.slice(match.index + match[0].length)
  const separator = match[0].includes('.') ? '.' : ','
  const formatted = Math.round(currentValue).toLocaleString(separator === '.' ? 'de-DE' : 'en-US')

  return `${prefix}${formatted}${suffix}`
}

function AnimatedAmount({ value, runKey }: { value: string; runKey: number }) {
  const match = value.match(/(\d[\d.,]*)/)
  const target = match ? Number(match[0].replace(/\D/g, '')) : 0
  const [currentValue, setCurrentValue] = useState(0)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    if (!runKey || !target) return undefined

    let frame = 0
    let startTime: number | null = null
    const duration = 2600
    const startDelay = 250
    setCurrentValue(0)
    setHasPlayed(true)
    setIsComplete(false)

    const tick = (time: number) => {
      if (startTime === null) startTime = time
      const progress = Math.min((time - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 2.7)
      setCurrentValue(target * eased)
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick)
      } else {
        setCurrentValue(target)
        setIsComplete(true)
      }
    }

    const timer = window.setTimeout(() => {
      frame = window.requestAnimationFrame(tick)
    }, startDelay)

    return () => {
      window.clearTimeout(timer)
      window.cancelAnimationFrame(frame)
    }
  }, [runKey, target])

  if (!match || !target) return <>{value}</>
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        transformOrigin: 'center',
        animation: isComplete ? 'amountCompletePop 720ms ease-out both' : 'none',
        '@keyframes amountCompletePop': {
          '0%': { transform: 'scale(1)' },
          '22%': { transform: 'scale(1.16) rotate(-1.2deg)' },
          '38%': { transform: 'scale(1.08) rotate(1.1deg)' },
          '52%': { transform: 'scale(1.13) rotate(-0.8deg)' },
          '68%': { transform: 'scale(1.04) rotate(0.5deg)' },
          '100%': { transform: 'scale(1) rotate(0deg)' },
        },
      }}
    >
      {formatAnimatedAmount(value, hasPlayed ? currentValue : target)}
    </Box>
  )
}

function getDisclosureButtonSx(
  active: boolean,
  accentColor: string,
  surfaceColor: string,
  borderColor: string,
) {
  return {
    alignSelf: 'flex-start',
    minHeight: 42,
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    px: 2,
    color: active ? 'common.white' : accentColor,
    border: `1px solid ${borderColor}`,
    borderColor,
    backgroundColor: active ? accentColor : BG_PRIMARY,
    boxShadow: 'none',
    '&:hover': {
      borderColor,
      backgroundColor: active ? accentColor : surfaceColor,
      boxShadow: 'none',
    },
  }
}

// NAV_LINKS được khởi tạo INSIDE TopBar component để dùng useTranslation hook.

// Eyebrow tag — navy uppercase letter-spaced (ICFGL academic feel)
function Eyebrow({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: '0.76rem',
        letterSpacing: 3,
        color: dark ? 'rgba(255,255,255,0.78)' : brand.hotPink,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  )
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'left',
}: {
  eyebrow: string
  title: string
  subtitle?: string
  align?: 'left' | 'center'
}) {
  return (
    <Stack
      spacing={2}
      alignItems={align === 'center' ? 'center' : 'flex-start'}
      textAlign={align === 'center' ? 'center' : 'left'}
      mb={{ xs: 3.5, md: 4.5 }}
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <Typography
        component="h2"
        sx={{
          color: INK,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: -0.5,
          fontSize: { xs: '1.75rem', md: '2.4rem' },
          lineHeight: 1.12,
        }}
      >
        {title}
      </Typography>
      {/* Horizontal divider rule — ICFGL marker */}
      <Box
        sx={{
          width: 64,
          height: 3,
          background: gradientBrand,
          borderRadius: 1,
          alignSelf: align === 'center' ? 'center' : 'flex-start',
        }}
      />
      {subtitle && (
        <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      )}
    </Stack>
  )
}

function SectionWatermark({
  number,
  side = 'left',
  dark = false,
  top = { xs: 18, md: 24 },
}: {
  number: string
  side?: 'left' | 'right' | 'center'
  dark?: boolean
  top?: { xs: number; md: number }
}) {
  const horizontal =
    side === 'right'
      ? { right: { xs: -18, md: 72 } }
      : side === 'center'
        ? { left: '50%', transform: 'translateX(-50%)' }
        : { left: { xs: -20, md: 72 } }

  return (
    <Typography
      aria-hidden
      sx={{
        position: 'absolute',
        top,
        ...horizontal,
        zIndex: 0,
        userSelect: 'none',
        pointerEvents: 'none',
        color: dark ? 'rgba(255,255,255,0.06)' : 'rgba(20,78,140,0.055)',
        fontFamily: '"Roboto Slab", Georgia, serif',
        fontWeight: 900,
        fontSize: { xs: '6.5rem', sm: '9rem', md: '13.5rem' },
        lineHeight: 0.8,
        letterSpacing: -8,
        whiteSpace: 'nowrap',
      }}
    >
      {number}
    </Typography>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Top Nav — white/blur khi scroll, transparent khi top hero
// ═══════════════════════════════════════════════════════════════════════
function TopBar({ isAuthenticated, scrolled }: { isAuthenticated: boolean; scrolled: boolean }) {
  const theme = useTheme()
  // Breakpoint `lg` (1200px) — desktop có thể vừa 7 link + switcher + login button;
  // dưới ngưỡng này dùng hamburger menu để tránh wrap.
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  // Trimmed nav (7 link essential) — các section còn lại (Schedule, Results,
  // Organizers, Teams, Partners) vẫn render trên trang, user scroll xuống xem.
  const NAV_LINKS = [
    { label: t('nav.home'), href: '#hero' },
    { label: t('nav.about'), href: '#overview' },
    { label: t('nav.timeline'), href: '#timeline' },
    { label: t('nav.rules'), href: '#rules' },
    { label: t('nav.awards'), href: '#awards' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ]

  const textOnHero = !scrolled

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          borderBottom: scrolled ? `1px solid ${HAIRLINE}` : '1px solid transparent',
          color: scrolled ? INK : 'common.white',
          transition: 'all 0.25s ease',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: '100%', mx: 'auto', minHeight: { xs: 60, md: 72 }, gap: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ minWidth: 0, flexShrink: 1 }}
          >
            <Box
              component="img"
              src="/moot-court-navbar-logo.jpg"
              alt="Phiên Toà Giả Định - Công Lý Giới"
              sx={{
                width: { xs: 42, md: 46 },
                height: { xs: 42, md: 46 },
                objectFit: 'contain',
                flexShrink: 0,
                filter: scrolled
                  ? 'drop-shadow(0 2px 6px rgba(20,78,140,0.2))'
                  : 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  letterSpacing: 0.5,
                  lineHeight: 1.1,
                  color: textOnHero ? 'common.white' : INK,
                }}
              >
                {t('brand.short')}
              </Typography>
              <Typography
                noWrap
                sx={{
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  color: brand.hotPink,
                  fontSize: '0.66rem',
                  letterSpacing: 2,
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                {t('brand.season')}
              </Typography>
            </Box>
          </Stack>

          {!isMobile ? (
            <Stack
              direction="row"
              spacing={0.25}
              sx={{ flexGrow: 1, justifyContent: 'center' }}
            >
              {NAV_LINKS.map((link) => (
                <Button
                  key={link.href}
                  href={link.href}
                  sx={{
                    color: textOnHero ? 'rgba(255,255,255,0.85)' : TEXT_BODY,
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    textTransform: 'none',
                    px: 1.1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: textOnHero ? 'common.white' : INK,
                      background: textOnHero ? 'rgba(255,255,255,0.08)' : 'rgba(20,78,140,0.06)',
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => setOpen((v) => !v)}
                sx={{ color: textOnHero ? 'common.white' : INK }}
              >
                {open ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          )}

          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <LanguageSwitcher dark={textOnHero} />
          </Box>

          <Button
            component={RouterLink}
            to={isAuthenticated ? '/dashboard' : '/login'}
            variant="contained"
            size="small"
            startIcon={<Login />}
            sx={{
              background: brand.hotPink,
              color: 'common.white',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              boxShadow: '0 6px 18px rgba(246,65,131,0.3)',
              '&:hover': { background: '#d9356f' },
            }}
          >
            {isAuthenticated ? t('topbar.dashboard') : t('topbar.login')}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Mobile drop-down */}
      {isMobile && open && (
        <Box
          sx={{
            position: 'fixed',
            top: 60,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${HAIRLINE}`,
            py: 2,
          }}
        >
          <Stack>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                sx={{
                  color: TEXT_BODY,
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 1.2,
                  borderRadius: 0,
                  '&:hover': { color: INK, background: 'rgba(20,78,140,0.05)' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Hero — template-inspired navy/pink mesh, without changing the top navigation.
// ═══════════════════════════════════════════════════════════════════════
function HeroSection({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { t } = useTranslation()
  const [activeHeroImage, setActiveHeroImage] = useState(0)
  const heroTitle = t('hero.title')
  const heroHighlight = t('hero.highlight')
  const [heroTitleBefore, heroTitleAfter] = heroHighlight
    ? heroTitle.split(heroHighlight)
    : [heroTitle, '']

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveHeroImage((prev) => (prev + 1) % heroBackgroundImages.length)
    }, 5000)

    return () => window.clearInterval(timer)
  }, [])

  return (
    <Box
      id="hero"
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        background: templateHeroBackground,
        color: 'common.white',
        pt: { xs: 12, md: 13 },
        pb: { xs: 8, md: 10 },
        overflow: 'hidden',
        '@keyframes heroSealSpin': {
          to: { transform: 'rotate(360deg)' },
        },
      }}
    >
      {heroBackgroundImages.map((src, index) => (
        <Box
          key={src}
          component="img"
          src={src}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: activeHeroImage === index ? 0.68 : 0,
            transition: 'opacity 1.1s ease',
            pointerEvents: 'none',
          }}
        />
      ))}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(120deg, rgba(7,27,51,0.7), rgba(20,78,140,0.38) 48%, rgba(7,27,51,0.56)), radial-gradient(circle at 56% 56%, rgba(255,255,255,0.06), transparent 14%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.18,
          backgroundImage:
            'linear-gradient(120deg, transparent, rgba(255,255,255,0.08), transparent), radial-gradient(circle at 35% 55%, rgba(246,65,131,0.28), transparent 18%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={{ xs: 3, md: 3.3 }} alignItems="flex-start" textAlign="left">
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.25,
                  border: `1px solid rgba(255,151,151,0.58)`,
                  background: 'rgba(255,255,255,0.1)',
                  color: 'common.white',
                  px: { xs: 1.6, sm: 2.4 },
                  py: 1,
                  borderRadius: 999,
                  maxWidth: '100%',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: TEMPLATE_CORAL,
                    boxShadow: '0 0 0 8px rgba(255,151,151,0.18)',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: 'common.white',
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: { xs: '0.68rem', sm: '0.78rem' },
                    letterSpacing: { xs: 1.6, sm: 2.6 },
                    lineHeight: 1.35,
                  }}
                >
                  {t('hero.subtitle')}
                </Typography>
              </Box>

              <Typography
                component="h1"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.02,
                  letterSpacing: -1,
                  fontSize: { xs: '2.6rem', sm: '3.8rem', md: '5rem' },
                  maxWidth: 860,
                  color: 'common.white',
                  textTransform: 'uppercase',
                  textShadow: '0 8px 32px rgba(0,0,0,0.22)',
                }}
              >
                {heroTitleBefore}
                {heroHighlight && heroTitle.includes(heroHighlight) ? (
                  <Box component="span" sx={{ color: TEMPLATE_CORAL }}>
                    {heroHighlight}
                  </Box>
                ) : null}
                {heroTitleAfter}
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.86)',
                  maxWidth: 720,
                  fontSize: { xs: '1rem', md: '1.17rem' },
                  lineHeight: 1.8,
                }}
              >
                {t('hero.tagline')}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1.5, width: { xs: '100%', sm: 'auto' } }}>
            {/* 2026-05-25: nút "Đăng ký ngay" mới — link đến Google Form
                đăng ký email đội trưởng. BTC nhận form → cấp tài khoản thủ
                công. URL trong [[siteContent.registrationFormUrl]] —
                placeholder rỗng cho đến khi legal team gửi link.
                Chỉ hiện cho khách chưa đăng nhập (authenticated user đã có
                account → không cần re-register). */}
            {!isAuthenticated && (
              <Tooltip title={t('hero.ctaRegister')}>
                <Button
                  component="a"
                  href={
                    siteContent.registrationFormUrl ||
                    'https://docs.google.com/forms/d/e/1FAIpQLSe7AXGzOp0rrxkAx1ipx3_fq0M0IFRj_i1BSClaQomLHPwlow/viewform'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  startIcon={<HowToReg />}
                  endIcon={<OpenInNew fontSize="small" />}
                  sx={{
                    minWidth: { xs: '100%', sm: 176, md: 190 },
                    background: TEMPLATE_PINK,
                    color: 'common.white',
                    fontWeight: 800,
                    px: { xs: 2.8, md: 4 },
                    py: 1.5,
                    fontSize: { xs: '0.94rem', md: '1rem' },
                    letterSpacing: 0.2,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    borderRadius: 999,
                    boxShadow: '0 14px 36px rgba(246,65,131,0.42)',
                    '&:hover': {
                      background: '#d62a6c',
                      boxShadow: '0 16px 40px rgba(246,65,131,0.5)',
                    },
                  }}
                >
                  {t('hero.ctaRegister')}
                </Button>
              </Tooltip>
            )}
            <Button
              href="#overview"
              variant="outlined"
              size="large"
              endIcon={<ArrowDownward />}
              sx={{
                minWidth: { xs: '100%', sm: 176, md: 190 },
                color: 'common.white',
                borderColor: 'rgba(255,255,255,0.58)',
                borderWidth: 2,
                fontWeight: 800,
                px: { xs: 2.8, md: 4 },
                py: 1.5,
                fontSize: { xs: '0.94rem', md: '1rem' },
                letterSpacing: 0.2,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                borderRadius: 999,
                backgroundColor: 'rgba(255,255,255,0.06)',
                '&:hover': {
                  borderColor: 'common.white',
                  borderWidth: 2,
                  backgroundColor: 'rgba(255,255,255,0.16)',
                },
              }}
            >
              {t('overview.title')}
            </Button>
            <Button
              href="#rules"
              variant="contained"
              size="large"
              endIcon={<ArrowDownward />}
              sx={{
                minWidth: { xs: '100%', sm: 176, md: 190 },
                color: TEMPLATE_CORAL,
                background: 'transparent',
                border: `2px solid ${TEMPLATE_CORAL}`,
                fontWeight: 800,
                px: { xs: 2.8, md: 4 },
                py: 1.5,
                fontSize: { xs: '0.94rem', md: '1rem' },
                letterSpacing: 0.2,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                borderRadius: 999,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                '&:hover': {
                  color: BG_NAVY_DARK,
                  background: TEMPLATE_CORAL,
                  borderColor: TEMPLATE_CORAL,
                  boxShadow: '0 10px 28px rgba(0,0,0,0.25)',
                },
              }}
            >
              {t('hero.ctaSecondary')}
            </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Box sx={{ display: 'flex', justifyContent: { sm: 'center', md: 'flex-end' } }}>
              <Box
                aria-hidden
                sx={{
                  position: 'relative',
                  width: { sm: 260, md: 340 },
                  height: { sm: 260, md: 340 },
                  color: 'common.white',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    animation: 'heroSealSpin 30s linear infinite',
                  }}
                >
                  <Box
                    component="svg"
                    viewBox="0 0 300 300"
                    sx={{ width: '100%', height: '100%', display: 'block' }}
                  >
                    <defs>
                      <path id="hero-seal-path" d="M150,150 m-124,0 a124,124 0 1,1 248,0 a124,124 0 1,1 -248,0" />
                    </defs>
                    <circle cx="150" cy="150" r="142" fill="none" stroke="rgba(255,151,151,0.54)" strokeWidth="1.5" />
                    <circle cx="150" cy="150" r="108" fill="none" stroke="rgba(255,151,151,0.46)" strokeWidth="1.5" />
                    <text
                      fill="rgba(255,255,255,0.92)"
                      fontSize="13"
                      fontWeight="800"
                      letterSpacing="3"
                      fontFamily='"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif'
                    >
                      <textPath href="#hero-seal-path" startOffset="0">
                        {`${t('hero.title')} ✦ ${t('brand.short')} ✦ `}
                      </textPath>
                    </text>
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <AccountBalance sx={{ fontSize: { sm: 66, md: 82 }, filter: 'drop-shadow(0 8px 18px rgba(246,65,131,0.42))' }} />
                  <Typography
                    sx={{
                      color: TEMPLATE_CORAL,
                      fontWeight: 900,
                      fontSize: { sm: '1.35rem', md: '1.7rem' },
                      letterSpacing: 4,
                      lineHeight: 1,
                    }}
                  >
                    {t('brand.season').replace(/\D/g, '')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Stats — white bg với số gradient brand
// ═══════════════════════════════════════════════════════════════════════
function StatsSection() {
  const { t } = useTranslation()
  // Stats values là metric cứng — giữ nguyên từ siteContent. Label dịch qua i18n.
  const statLabelKeys = ['stats.teams', 'stats.universities', 'stats.judges', 'stats.rounds'] as const
  const stats = siteContent.stats.map((s, idx) => ({
    value: s.value,
    label: t(statLabelKeys[idx] ?? 'stats.teams'),
  }))

  return (
    <Box sx={{ background: BG_SOFT, borderBottom: `1px solid ${HAIRLINE}`, py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 0 }}>
          {stats.map((s, idx) => (
            <Grid
              item
              xs={6}
              md={3}
              key={statLabelKeys[idx] ?? `stat-${idx}`}
              sx={{
                textAlign: 'center',
                borderRight: {
                  md: idx < stats.length - 1 ? `1px solid ${HAIRLINE}` : 'none',
                },
                py: { xs: 2, md: 3 },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  letterSpacing: -1,
                  background: gradientBrand,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.05,
                }}
              >
                {s.value}
              </Typography>
              <Typography
                sx={{
                  color: TEXT_DIM,
                  fontSize: '0.78rem',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  mt: 1,
                }}
              >
                {s.label}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Overview — white bg
// ═══════════════════════════════════════════════════════════════════════
function OverviewSection() {
  const { t, i18n } = useTranslation()
  const icons = [<Gavel />, <AccountBalance />, <Diversity3 />, <EmojiEvents />]
  const highlights = t('overview.highlights', { returnObjects: true }) as Array<{
    title: string
    description: string
  }>
  const slogan = t('overview.slogan')
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [overviewRef, overviewEntered] = useOnceInView<HTMLDivElement>()

  return (
    <Box
      ref={overviewRef}
      id="overview"
      sx={{
        py: { xs: 8, md: 12 },
        background: BG_PRIMARY,
        position: 'relative',
        overflow: 'hidden',
        ...sectionTexture,
      }}
    >
      <SectionWatermark number="01" side="left" />
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 3.5, md: 6 }}
          alignItems="flex-start"
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Grid item xs={12} lg={5}>
            <Stack spacing={2.2} sx={{ position: { lg: 'sticky' }, top: { lg: 104 } }}>
              <Eyebrow>{t('overview.eyebrow')}</Eyebrow>
              <Typography
                component="h2"
                sx={{
                  color: INK,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: -0.5,
                  fontSize: { xs: '2.25rem', md: '3.45rem' },
                  lineHeight: 1.06,
                }}
              >
                {t('overview.title')}
              </Typography>
              <Box sx={{ width: 78, height: 5, borderRadius: 999, backgroundColor: brand.hotPink }} />
              <Typography
                sx={{
                  color: TEXT_DIM,
                  fontSize: { xs: '1rem', md: '1.08rem' },
                  lineHeight: 1.72,
                  maxWidth: 760,
                }}
              >
                {t('overview.description')}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  mt: 1,
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  background: 'linear-gradient(160deg, #144E8C, #0C2E55)',
                  color: 'common.white',
                  boxShadow: '0 26px 60px rgba(20,78,140,0.22)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '"\\201C"',
                    position: 'absolute',
                    top: 18,
                    left: 22,
                    color: TEMPLATE_PINK,
                    fontSize: { xs: 64, md: 92 },
                    fontWeight: 900,
                    lineHeight: 1,
                    opacity: 0.42,
                  },
                }}
              >
              <Typography
                sx={{
                    color: 'common.white',
                  fontStyle: 'italic',
                  fontSize: { xs: '1.15rem', md: '1.38rem' },
                  fontWeight: 700,
                  lineHeight: 1.7,
                  letterSpacing: -0.2,
                    position: 'relative',
                    pt: 2,
                }}
              >
                {i18n.language === 'en' && slogan.includes('\n')
                  ? slogan.split('\n').map((line, index) => (
                      <Fragment key={line}>
                        {index > 0 ? <br /> : null}
                        {line}
                      </Fragment>
                    ))
                  : slogan}
              </Typography>
            </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Stack spacing={{ xs: 1.6, md: 1.8 }}>
          {highlights.map((item, index) => {
            const accent = overviewAccents[index % overviewAccents.length]
            const isExpanded = expandedIndex === index
            const detailSurface = accent.accent === ACCENT_PINK ? PINK_TINT_04 : BLUE_TINT_04
            const descriptionParagraphs = item.description
              .split('\n')
              .map((part) => part.trim())
              .filter(Boolean)

            return (
              <Paper
                key={item.title}
                elevation={0}
                sx={{
                  px: { xs: 1.75, md: 2 },
                  py: { xs: 1.7, md: 1.95 },
                  borderRadius: 3,
                  background: BG_PRIMARY,
                  border: `1px solid ${isExpanded ? accent.accent : '#FFD6E4'}`,
                  minHeight: { xs: 'auto', md: 126 },
                  boxShadow: isExpanded ? '0 22px 48px rgba(246,65,131,0.13)' : CARD_SHADOW,
                  opacity: overviewEntered ? 1 : 0,
                  animation: overviewEntered
                    ? `overviewCardDrop 1450ms cubic-bezier(0.2, 0.8, 0.2, 1) ${250 + index * 170}ms both`
                    : 'none',
                  transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
                  '@keyframes overviewCardDrop': {
                    '0%': { opacity: 0, transform: 'translateY(-38px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    borderColor: accent.accent,
                    boxShadow: CARD_SHADOW_HOVER,
                  },
                }}
              >
                <Stack spacing={isExpanded ? 1.15 : 0}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 188px' },
                      gap: { xs: 1.2, md: 1.5 },
                      alignItems: 'stretch',
                      minHeight: { xs: 'auto', md: 78 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1.8,
                            background: isExpanded ? accent.accent : BG_PINK,
                            color: isExpanded ? 'common.white' : accent.iconColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {icons[index % icons.length]}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: accent.accent,
                              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                              fontSize: '0.74rem',
                              letterSpacing: 1.15,
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              mb: 0.18,
                            }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </Typography>
                          <Typography
                            sx={{
                              color: index % 2 === 1 ? ACCENT_PINK : INK,
                              fontWeight: 800,
                              fontSize: { xs: '1.1rem', md: '1.15rem' },
                              lineHeight: 1.22,
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      <IconButton
                        onClick={() => setExpandedIndex((prev) => (prev === index ? null : index))}
                        aria-expanded={isExpanded}
                        aria-controls={`overview-key-information-${index}`}
                        aria-label={t('common.keyInformation')}
                        sx={{
                          width: { xs: 48, md: 54 },
                          height: { xs: 48, md: 54 },
                          borderRadius: 2,
                          color: isExpanded ? 'common.white' : accent.accent,
                          backgroundColor: isExpanded ? accent.accent : BG_PRIMARY,
                          border: 'none',
                          fontSize: { xs: '2rem', md: '2.3rem' },
                          fontWeight: 400,
                          lineHeight: 1,
                          transition: 'border-color 0.22s ease, background-color 0.22s ease',
                          '&:hover': {
                            backgroundColor: isExpanded ? accent.accent : detailSurface,
                          },
                        }}
                      >
                        {isExpanded ? '×' : '+'}
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateRows: isExpanded ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.3s ease',
                    }}
                  >
                    <Box id={`overview-key-information-${index}`} sx={{ overflow: 'hidden' }}>
                          <Paper
                            elevation={0}
                            sx={{
                          mt: isExpanded ? 1.2 : 0,
                          p: isExpanded ? { xs: 1.45, md: 1.7 } : 0,
                          borderRadius: 3,
                          background: isExpanded ? detailSurface : 'transparent',
                          border: isExpanded ? `1px solid ${accent.accent}24` : '1px solid transparent',
                          boxShadow: 'none',
                        }}
                      >
                            <Box
                              sx={{
                                width: '100%',
                                p: { xs: 1.1, md: 1.25 },
                                borderRadius: 2.5,
                                background: BG_PRIMARY,
                                border: `1px solid ${accent.accent}14`,
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={0.8} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: accent.accent,
                                      flexShrink: 0,
                                      boxShadow: `0 0 0 4px ${detailSurface}`,
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 1,
                                      borderRadius: 999,
                                      backgroundColor: `${accent.accent}18`,
                                    }}
                                  />
                                </Stack>

                                <Stack spacing={0.95}>
                                  {descriptionParagraphs.map((paragraph, paragraphIndex) => (
                                    <Typography
                                      key={`${item.title}-paragraph-${paragraphIndex}`}
                                      sx={{
                                        color: TEXT_BODY,
                                        fontSize: { xs: '0.92rem', md: '0.95rem' },
                                        lineHeight: 1.74,
                                      }}
                                    >
                                      {paragraph}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Stack>
                            </Box>
                      </Paper>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            )
          })}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Timeline — off-white section, 3 phase cards
// ═══════════════════════════════════════════════════════════════════════
function TimelineSection() {
  const { t } = useTranslation()
  const phases = t('timeline.phases', { returnObjects: true }) as Array<{
    code: string
    title: string
    date: string
    description: string
    description2?: string
    status?: 'opening' | 'comingSoon' | 'completed'
  }>
  const statusLabels = t('timeline.statusLabels', { returnObjects: true }) as Record<string, string>
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const getStatusSx = (status: string) => {
    switch (status) {
      case 'opening':
        return {
          color: ACCENT_PINK,
          background: PINK_TINT_10,
          border: `1px solid ${PINK_TINT_18}`,
        }
      case 'completed':
        return {
          color: INK,
          background: BLUE_TINT_10,
          border: `1px solid ${BLUE_TINT_18}`,
        }
      case 'comingSoon':
      default:
        return {
          color: INK,
          background: BLUE_TINT_04,
          border: `1px solid ${BLUE_TINT_18}`,
        }
    }
  }

  return (
    <Box
      id="timeline"
      sx={{
        py: { xs: 8, md: 12 },
        background: BG_SOFT,
        position: 'relative',
        overflow: 'hidden',
        ...sectionTexture,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 4, md: 6 } }}>
          <Eyebrow>{t('timeline.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: INK,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.08,
              maxWidth: 760,
              background: `linear-gradient(100deg, ${INK}, ${TEMPLATE_PINK})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('timeline.title')}
          </Typography>
          <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
            {t('timeline.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: { xs: 2, md: 2.5 },
            alignItems: 'start',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: { md: 35 },
              left: { md: '7%' },
              right: { md: '7%' },
              height: 3,
              display: { xs: 'none', md: 'block' },
              background: BG_PINK,
              borderRadius: 999,
              zIndex: 0,
            }}
          />
          {phases.map((p, idx) => {
            const statusKey = p.status || 'comingSoon'
            const statusLabel = statusLabels[statusKey] || statusKey
            const accent = timelineAccents[idx % timelineAccents.length]
            const isExpanded = expandedIndex === idx

            return (
              <Paper
                key={p.code}
                elevation={0}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: { xs: 2.4, md: 3 },
                  minHeight: { xs: 'auto', md: isExpanded ? 342 : 276 },
                  borderRadius: 3,
                  textAlign: 'center',
                  background: BG_PRIMARY,
                  border: `1px solid ${isExpanded ? accent.accent : '#FFD6E4'}`,
                  boxShadow: isExpanded ? '0 28px 56px rgba(246,65,131,0.16)' : CARD_SHADOW,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  transition: 'border-color 0.24s ease, box-shadow 0.24s ease',
                  '&:hover': {
                    borderColor: accent.accent,
                    boxShadow: CARD_SHADOW_HOVER,
                  },
                }}
                onClick={() => setExpandedIndex((prev) => (prev === idx ? null : idx))}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: '50%',
                    background: `linear-gradient(150deg, ${TEMPLATE_PINK}, #d62a6c)`,
                    color: 'common.white',
                    fontWeight: 900,
                    fontSize: '1.15rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    border: `7px solid ${BG_PRIMARY}`,
                    outline: `2px solid ${BG_PINK}`,
                    boxShadow: '0 10px 24px rgba(246,65,131,0.3)',
                  }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </Box>

                <Typography sx={{ color: INK, fontWeight: 900, fontSize: '1.1rem', lineHeight: 1.25, mb: 1.2 }}>
                  {p.title}
                </Typography>
                <Chip label={statusLabel} size="small" sx={{ mb: 1.2, fontWeight: 800, ...getStatusSx(statusKey) }} />
                <Typography
                  sx={{
                    color: TEXT_DIM,
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    lineHeight: 1.45,
                    mb: 1.4,
                  }}
                >
                  {p.date}
                </Typography>

                <IconButton
                  aria-expanded={isExpanded}
                  aria-controls={`timeline-key-information-${idx}`}
                  aria-label={t('common.keyInformation')}
                  sx={{
                    mt: 'auto',
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    color: isExpanded ? 'common.white' : TEMPLATE_PINK,
                    backgroundColor: isExpanded ? TEMPLATE_PINK : BG_PINK,
                    border: `1px solid ${TEMPLATE_PINK}`,
                    fontSize: '1.9rem',
                    lineHeight: 1,
                    '&:hover': {
                      backgroundColor: isExpanded ? TEMPLATE_PINK : PINK_TINT_10,
                      borderColor: TEMPLATE_PINK,
                    },
                  }}
                >
                  {isExpanded ? '×' : '+'}
                </IconButton>

                <Box
                  sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                    transition: 'grid-template-rows 0.3s ease',
                  }}
                >
                  <Box id={`timeline-key-information-${idx}`} sx={{ overflow: 'hidden' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1.8,
                        p: { xs: 1.3, md: 1.45 },
                        borderRadius: 2.4,
                        background: idx % 2 === 0 ? BLUE_TINT_04 : PINK_TINT_04,
                        border: `1px solid ${accent.accent}20`,
                        textAlign: 'left',
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography sx={{ color: TEXT_BODY, fontSize: '0.9rem', lineHeight: 1.65 }}>
                          {p.description}
                        </Typography>
                        {p.description2 ? (
                          <Typography sx={{ color: TEXT_BODY, fontSize: '0.9rem', lineHeight: 1.65 }}>
                            {p.description2}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Paper>
                  </Box>
                </Box>
              </Paper>
            )
          })}
        </Box>

      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Competition Timeline — thay block Lịch các phiên thi
// ═══════════════════════════════════════════════════════════════════════
function getStatusSx(status: string) {
  switch (status) {
    case 'opening':
      return {
        color: 'common.white',
        background: TEMPLATE_PINK,
        border: `1px solid ${TEMPLATE_PINK}`,
      }
    case 'completed':
      return {
        color: 'common.white',
        background: SOLID_BLUE,
        border: `1px solid ${SOLID_BLUE}`,
      }
    case 'comingSoon':
    default:
      return {
        color: TEMPLATE_PINK,
        background: BG_PRIMARY,
        border: `1px solid ${TEMPLATE_PINK}`,
      }
  }
}

function ScheduleSection() {
  const { t } = useTranslation()
  const phaseIcons = [<HowToReg />, <Article />, <Gavel />, <EmojiEvents />]
  const timelineIcons = [<HowToReg />, <Login />, <Groups />, <Schedule />, <Article />, <Gavel />, <Diversity3 />, <AccountBalance />, <EmojiEvents />]
  const items = t('competitionTimeline.items', { returnObjects: true }) as Array<{
    title: string
    date: string
    details?: string[]
    status?: 'opening' | 'comingSoon' | 'completed'
  }>
  const groupedLabels = t('competitionTimeline.grouped', { returnObjects: true }) as Record<string, string>
  const [scheduleRef, scheduleEntered] = useOnceInView<HTMLDivElement>()
  const [currentDate, setCurrentDate] = useState(() => new Date())
  const [selectedDetail, setSelectedDetail] = useState<{
    title: string
    date: string
    details: string[]
    accent: string
    label: string
  } | null>(null)

  type PhaseEntry = {
    title?: string
    date: string
    badge?: string
    icon: ReactNode
    details?: string[]
    start?: string
    end?: string
  }

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentDate(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const isNowInRange = (start?: string, end?: string) => {
    if (!start) return false
    const startTime = new Date(start).getTime()
    const endTime = end ? new Date(end).getTime() : startTime + 86_399_999
    const now = currentDate.getTime()
    return now >= startTime && now <= endTime
  }

  const phaseGroups = [
    {
      label: groupedLabels.phase1Label,
      title: groupedLabels.phase1Title,
      date: '24/06 - 24/07/2026',
      start: '2026-06-24T00:00:00+07:00',
      end: '2026-07-24T23:59:59+07:00',
      accent: SOLID_BLUE,
      surface: BLUE_TINT_04,
      entries: [
        { title: items[0]?.title, date: '24/06 -> 15/07', icon: timelineIcons[0], start: '2026-06-24T00:00:00+07:00', end: '2026-07-15T23:59:59+07:00' },
        { title: items[1]?.title, date: '16/07 -> 18/07', icon: timelineIcons[1], start: '2026-07-16T00:00:00+07:00', end: '2026-07-18T23:59:59+07:00' },
        { title: items[2]?.title, date: '18/07 -> 22/07', icon: timelineIcons[2], start: '2026-07-18T00:00:00+07:00', end: '2026-07-22T23:59:59+07:00' },
        { title: groupedLabels.closePlatformInfo, date: '23:59, 22/07', icon: <Schedule />, start: '2026-07-22T23:59:00+07:00', end: '2026-07-22T23:59:59+07:00' },
        { title: items[3]?.title, date: '19:00, 24/07', badge: groupedLabels.trainingBadge, icon: timelineIcons[3], details: items[3]?.details, start: '2026-07-24T19:00:00+07:00', end: '2026-07-24T21:00:00+07:00' },
      ],
    },
    {
      label: groupedLabels.phase2Label,
      title: groupedLabels.phase2Title,
      date: '25/07 - 20/08/2026',
      start: '2026-07-25T00:00:00+07:00',
      end: '2026-08-20T23:59:59+07:00',
      accent: '#2388C8',
      surface: BLUE_TINT_04,
      entries: [
        { title: groupedLabels.openCaseMaterials, date: '08:00, 25/07', icon: <Article />, details: [groupedLabels.openCaseMaterialsDetail1, groupedLabels.openCaseMaterialsDetail2].filter(Boolean) as string[], start: '2026-07-25T08:00:00+07:00', end: '2026-07-25T09:00:00+07:00' },
        { title: items[5]?.title, date: '09:00, 26/07', badge: groupedLabels.trainingBadge, icon: timelineIcons[5], details: items[5]?.details, start: '2026-07-26T09:00:00+07:00', end: '2026-07-26T11:00:00+07:00' },
        { title: items[6]?.title, date: '19:00, 29/07', badge: groupedLabels.trainingBadge, icon: timelineIcons[6], details: items[6]?.details, start: '2026-07-29T19:00:00+07:00', end: '2026-07-29T21:00:00+07:00' },
        { title: groupedLabels.preliminarySubmissionDeadline, date: '25/07 -> 17/08', icon: timelineIcons[4], start: '2026-07-25T08:00:00+07:00', end: '2026-08-17T23:59:59+07:00' },
        { title: groupedLabels.preliminaryResults, date: '20/08', icon: <CheckCircle />, start: '2026-08-20T00:00:00+07:00', end: '2026-08-20T23:59:59+07:00' },
      ],
    },
    {
      label: groupedLabels.phase3Label,
      title: items[7]?.title || 'Vòng Bán kết',
      date: '20/08 - 01/09/2026',
      start: '2026-08-20T00:00:00+07:00',
      end: '2026-09-01T23:59:59+07:00',
      accent: '#C23A76',
      surface: PINK_TINT_04,
      entries: [
        { title: groupedLabels.roleSheetSubmission, date: '20/08 -> 09:00, 27/08', icon: <Groups />, start: '2026-08-20T00:00:00+07:00', end: '2026-08-27T09:00:00+07:00' },
        { title: groupedLabels.evidenceMediationSession, date: '09:00, 30/08', icon: <Gavel />, details: [items[7]?.details?.[1], items[7]?.details?.[2]].filter(Boolean) as string[], start: '2026-08-30T09:00:00+07:00', end: '2026-08-30T12:00:00+07:00' },
        { title: groupedLabels.semiFinalResults, date: '01/09', icon: <CheckCircle />, start: '2026-09-01T00:00:00+07:00', end: '2026-09-01T23:59:59+07:00' },
      ],
    },
    {
      label: groupedLabels.phase4Label,
      title: items[8]?.title || 'Vòng Chung kết',
      date: '09:00, 20/09/2026',
      start: '2026-09-20T09:00:00+07:00',
      end: '2026-09-20T23:59:59+07:00',
      accent: TEMPLATE_PINK,
      surface: PINK_TINT_04,
      entries: [
        { title: groupedLabels.finalTrial, date: '09:00, 20/09', icon: <AccountBalance />, details: items[8]?.details, start: '2026-09-20T09:00:00+07:00', end: '2026-09-20T12:00:00+07:00' },
        { title: groupedLabels.awardCeremony, date: '20/09', icon: <EmojiEvents />, start: '2026-09-20T00:00:00+07:00', end: '2026-09-20T23:59:59+07:00' },
      ],
    },
  ].map((phase) => ({
    ...phase,
    entries: (phase.entries as PhaseEntry[]).filter((entry) => Boolean(entry.title)),
  }))
  const activePhaseIndex = phaseGroups.reduce((activeIndex, phase, index) => {
    return isNowInRange(phase.start, phase.end) ? index : activeIndex
  }, -1)

  return (
    <Box
      ref={scheduleRef}
      id="timeline"
      sx={{
        pt: { xs: 3, md: 4 },
        pb: { xs: 8, md: 12 },
        background: BG_PINK,
        position: 'relative',
        overflow: 'hidden',
        ...sectionTexture,
      }}
    >
      <SectionWatermark number="02" side="center" top={{ xs: -8, md: -34 }} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 4, md: 5 } }}>
          <Eyebrow>{t('competitionTimeline.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: INK,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '2.25rem', md: '3.35rem' },
              lineHeight: 1.06,
              maxWidth: 920,
              mx: 'auto',
            }}
          >
            {t('timeline.title')}
          </Typography>
          <Typography sx={{ color: TEXT_DIM, maxWidth: 780, fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.7 }}>
            {t('competitionTimeline.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={{
            mt: { xs: 0.5, md: 0.8 },
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            spacing={{ xs: 0.8, md: 1.55 }}
            useFlexGap
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            sx={{ mb: { xs: 2.2, md: 3 }, transform: { xs: 'none', md: 'translateX(38px)' } }}
          >
            {phaseGroups.map((phase, idx) => {
              const phaseActive = idx === activePhaseIndex

              return (
              <Stack key={`legend_${phase.label}`} direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: { xs: 8, md: 9 },
                    height: { xs: 8, md: 9 },
                    borderRadius: '50%',
                    backgroundColor: phase.accent,
                    boxShadow: phaseActive ? `0 0 0 4px ${phase.accent}22` : `0 0 0 3px ${phase.accent}14`,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ color: '#1F2933', fontWeight: phaseActive ? 650 : 500, fontSize: { xs: '0.76rem', md: '0.86rem' } }}>
                  {phase.label} · {phase.title}
                </Typography>
              </Stack>
              )
            })}
          </Stack>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.35, md: 1.25 },
              maxWidth: { xs: 940, md: 980 },
              mx: 'auto',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 22,
                bottom: 22,
                left: { xs: 22, md: 38 },
                width: 2,
                borderRadius: 999,
                background: 'linear-gradient(180deg, rgba(20,78,140,0.16), rgba(246,65,131,0.24))',
                zIndex: 0,
              },
            }}
          >
            {phaseGroups.map((phase, idx) => {
              const phaseActive = idx === activePhaseIndex
              const phaseReached = phase.start ? currentDate.getTime() >= new Date(phase.start).getTime() : false

              return (
                <Box
                  key={phase.label}
                  sx={{
                    position: 'relative',
                    display: 'grid',
                    gridTemplateColumns: { xs: '46px minmax(0, 1fr)', md: '76px minmax(0, 1fr)' },
                    gap: { xs: 1.25, md: 1.35 },
                    alignItems: 'start',
                    zIndex: 1,
                    opacity: scheduleEntered ? 1 : 0,
                    animation: scheduleEntered
                      ? `journeyStepIn 1200ms cubic-bezier(0.2, 0.8, 0.2, 1) ${220 + idx * 160}ms both`
                      : 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: phaseReached ? { xs: -12, md: -18 } : 0,
                      bottom: phaseReached ? { xs: -22, md: -28 } : 'auto',
                      left: { xs: 22, md: 38 },
                      width: phaseReached ? 3 : 2,
                      borderRadius: 999,
                      backgroundColor: phaseReached ? phase.accent : 'transparent',
                      transform: 'translateX(-50%)',
                      zIndex: 0,
                    },
                    '@keyframes journeyStepIn': {
                      '0%': { opacity: 0, transform: 'translateY(34px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Box
                    sx={{
                      gridColumn: '1',
                      display: 'flex',
                      justifyContent: 'center',
                      pt: { xs: 0.2, md: 0.15 },
                      zIndex: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 42, md: 48 },
                        height: { xs: 42, md: 48 },
                        borderRadius: '50%',
                        background: phase.accent,
                        color: 'common.white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 900,
                        border: `5px solid ${phaseActive ? BG_PRIMARY : BG_PINK}`,
                        boxShadow: phaseActive ? `0 0 0 8px ${phase.accent}24, 0 14px 28px ${phase.accent}42` : `0 12px 24px ${phase.accent}2e`,
                      }}
                    >
                      {idx + 1}
                    </Box>
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      gridColumn: '2',
                      overflow: 'hidden',
                      borderRadius: 2.7,
                      background: BG_PRIMARY,
                      border: `1px solid ${phase.accent}55`,
                      boxShadow: '0 16px 34px rgba(20,78,140,0.07)',
                      transition: 'box-shadow 0.22s ease',
                    }}
                  >
                    <Box
                      sx={{
                        px: { xs: 1.35, md: 1.8 },
                        py: { xs: 1.15, md: 1.25 },
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto' },
                        gap: { xs: 0.65, md: 1.05 },
                        alignItems: 'center',
                        background: phaseActive
                          ? `linear-gradient(135deg, ${phase.accent}, ${phase.accent}e6)`
                          : phase.accent,
                        color: 'common.white',
                      }}
                    >
                      <Stack direction="row" spacing={0.85} alignItems="center">
                        <Box
                          sx={{
                            px: 1,
                            py: 0.35,
                            borderRadius: 999,
                            background: 'rgba(255,255,255,0.18)',
                            color: 'common.white',
                            fontWeight: 900,
                            fontSize: '0.78rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {phase.label}
                        </Box>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            background: 'rgba(255,255,255,0.16)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            '& svg': { fontSize: 21 },
                          }}
                        >
                          {phaseIcons[idx]}
                        </Box>
                      </Stack>
                      <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.05rem', md: '1.22rem' }, lineHeight: 1.22 }}>
                        {phase.title}
                      </Typography>
                      <Typography
                        sx={{
                          justifySelf: { xs: 'start', md: 'end' },
                          color: 'rgba(255,255,255,0.86)',
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontWeight: 850,
                          fontSize: { xs: '0.88rem', md: '0.98rem' },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {phase.date}
                      </Typography>
                    </Box>

                    <Stack divider={<Box sx={{ height: 1, backgroundColor: 'rgba(20,78,140,0.08)' }} />}>
                      {phase.entries.map((entry, entryIndex) => {
                        const hasEntryDetails = Boolean(entry.details?.length)
                        const entryActive = isNowInRange(entry.start, entry.end)

                        return (
                        <Box
                          key={`${phase.label}_${entry.title}`}
                          role={hasEntryDetails ? 'button' : undefined}
                          tabIndex={hasEntryDetails ? 0 : undefined}
                          onClick={
                            hasEntryDetails
                              ? () =>
                                  setSelectedDetail({
                                    title: entry.title || '',
                                    date: entry.date,
                                    details: entry.details || [],
                                    accent: phase.accent,
                                    label: phase.label,
                                  })
                              : undefined
                          }
                          onKeyDown={
                            hasEntryDetails
                              ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    setSelectedDetail({
                                      title: entry.title || '',
                                      date: entry.date,
                                      details: entry.details || [],
                                      accent: phase.accent,
                                      label: phase.label,
                                    })
                                  }
                                }
                              : undefined
                          }
                          sx={{
                            px: { xs: 1.35, md: 1.8 },
                            py: { xs: 1.05, md: 1.15 },
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                            gap: { xs: 0.55, sm: 1.25 },
                            alignItems: 'center',
                            cursor: hasEntryDetails ? 'pointer' : 'default',
                            backgroundColor: entryActive ? `${phase.accent}10` : entryIndex % 2 === 0 ? BG_PRIMARY : 'rgba(20,78,140,0.018)',
                            borderLeft: entryActive ? `4px solid ${phase.accent}` : '4px solid transparent',
                            transition: 'background-color 0.18s ease',
                            '&:hover': hasEntryDetails
                              ? {
                                  backgroundColor: `${phase.accent}08`,
                                }
                              : undefined,
                            '&:focus-visible': hasEntryDetails
                              ? {
                                  outline: `2px solid ${phase.accent}`,
                                  outlineOffset: -2,
                                }
                              : undefined,
                          }}
                        >
                          <Stack direction="row" spacing={0.9} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                mt: 0.05,
                                borderRadius: '50%',
                                backgroundColor: entryActive ? phase.accent : `${phase.accent}14`,
                                color: entryActive ? 'common.white' : phase.accent,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                '& svg': { fontSize: 17 },
                              }}
                            >
                              {entry.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ color: entryActive ? phase.accent : INK, fontWeight: entryActive ? 900 : 850, fontSize: { xs: '0.98rem', md: '1.05rem' }, lineHeight: 1.35 }}>
                                {entry.title}
                              </Typography>
                              {entry.badge ? (
                                <Box
                                  sx={{
                                    mt: 0.45,
                                    width: 'fit-content',
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 1,
                                    color: phase.accent,
                                    backgroundColor: `${phase.accent}12`,
                                    fontWeight: 850,
                                    fontSize: '0.72rem',
                                    lineHeight: 1,
                                  }}
                                >
                                  {entry.badge}
                                </Box>
                              ) : null}
                              {entryActive ? (
                                <Box
                                  sx={{
                                    mt: 0.45,
                                    width: 'fit-content',
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 1,
                                    color: 'common.white',
                                    backgroundColor: phase.accent,
                                    fontWeight: 850,
                                    fontSize: '0.72rem',
                                    lineHeight: 1,
                                  }}
                                >
                                  {t('competitionTimeline.activeLabel')}
                                </Box>
                              ) : null}
                            </Box>
                          </Stack>
                          <Stack spacing={0.45} alignItems={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ justifySelf: { xs: 'start', sm: 'end' } }}>
                            <Typography
                              sx={{
                                color: phase.accent,
                                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontWeight: 900,
                                fontSize: { xs: '0.9rem', md: '0.98rem' },
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {entry.date}
                            </Typography>
                            {hasEntryDetails ? (
                              <Box
                                sx={{
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 999,
                                  color: phase.accent,
                                  backgroundColor: `${phase.accent}12`,
                                  fontWeight: 850,
                                  fontSize: '0.68rem',
                                  lineHeight: 1,
                                }}
                              >
                                {t('competitionTimeline.detailCta')}
                              </Box>
                            ) : null}
                          </Stack>
                        </Box>
                        )
                      })}
                    </Stack>
                  </Paper>
                </Box>
              )
            })}
          </Box>
        </Box>

        {selectedDetail ? (
          <Box
            role="presentation"
            onClick={() => setSelectedDetail(null)}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 1.5, md: 3 },
              backgroundColor: 'rgba(12,24,38,0.34)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Paper
              elevation={0}
              role="dialog"
              aria-modal="true"
              aria-label={selectedDetail.title}
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: '100%',
                maxWidth: 620,
                maxHeight: 'min(82dvh, 680px)',
                overflow: 'auto',
                borderRadius: 3,
                background: BG_PRIMARY,
                border: `1px solid ${selectedDetail.accent}55`,
                boxShadow: '0 28px 72px rgba(12,24,38,0.28)',
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.7, md: 2.2 },
                  py: { xs: 1.55, md: 1.85 },
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1.2,
                  alignItems: 'start',
                  background: `linear-gradient(135deg, ${selectedDetail.accent}, ${selectedDetail.accent}dd)`,
                  color: 'common.white',
                }}
              >
                <Box>
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.85 }}>
                    <Box
                      sx={{
                        px: 0.9,
                        py: 0.3,
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.18)',
                        fontWeight: 900,
                        fontSize: '0.76rem',
                      }}
                    >
                      {selectedDetail.label}
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 850, fontSize: '0.86rem' }}>
                      {selectedDetail.date}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.22rem', md: '1.45rem' }, lineHeight: 1.22 }}>
                    {selectedDetail.title}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setSelectedDetail(null)}
                  aria-label="Đóng"
                  sx={{
                    color: 'common.white',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Stack spacing={1.05} sx={{ p: { xs: 1.7, md: 2.2 } }}>
                {selectedDetail.details.map((detail) => (
                  <Stack key={detail} direction="row" spacing={0.9} alignItems="flex-start">
                    <CheckCircle sx={{ color: selectedDetail.accent, fontSize: 18, mt: 0.18, flexShrink: 0 }} />
                    <Typography sx={{ color: TEXT_BODY, fontSize: { xs: '0.94rem', md: '1rem' }, lineHeight: 1.65 }}>
                      {detail}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>
        ) : null}
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Competition Highlights & Rankings
// ═══════════════════════════════════════════════════════════════════════
function getCompetitionStatusLabel(
  roundType: string | null | undefined,
  competitionStatus: string | null | undefined,
  t: (key: string) => string,
) {
  if (roundType === 'final' || competitionStatus === 'advanced_to_final') {
    return t('highlightsSection.status.finalist')
  }
  return t('highlightsSection.status.qualified')
}

function ResultsCaseCard({ caseId, title }: { caseId: string; title: string }) {
  const { t, i18n } = useTranslation()
  const { data, isLoading } = useGetPublicLatestResultsQuery(caseId, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.4, md: 3 },
          background: BG_PRIMARY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 3.5,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    )
  }

  if (!data) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          background: BG_PRIMARY,
          border: `1px dashed ${HAIRLINE}`,
          borderRadius: 3.5,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: INK, fontWeight: 800, mb: 0.75 }}>{title}</Typography>
        <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem', lineHeight: 1.65 }}>
          {t('highlightsSection.pending')}
        </Typography>
      </Paper>
    )
  }

  const publishedDate = data.published_at
    ? new Date(data.published_at).toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US')
    : '—'
  const rows = data.advancing_teams.slice(0, 4)

  return (
    <Paper
      elevation={0}
      sx={{
        background: BG_PRIMARY,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 3.5,
        overflow: 'hidden',
        transition: 'border-color 0.22s ease, background-color 0.22s ease',
        '&:hover': {
          borderColor: SOLID_BLUE,
          backgroundColor: '#FEFFFF',
        },
      }}
    >
      <Stack spacing={0}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{
            p: { xs: 2.2, md: 2.55 },
            background: BLUE_TINT_04,
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: SOLID_BLUE,
                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                fontSize: '0.74rem',
                letterSpacing: 1.4,
                fontWeight: 900,
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              {data.round?.round_name || t('highlightsSection.latestRound')}
            </Typography>
            <Typography sx={{ color: INK, fontWeight: 900, fontSize: { xs: '1.08rem', md: '1.18rem' }, lineHeight: 1.28 }}>
              {data.case_title || title}
            </Typography>
          </Box>
          <Chip
            label={`${t('highlightsSection.publishedLabel')} ${publishedDate}`}
            size="small"
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              fontWeight: 800,
              color: SOLID_BLUE,
              background: BG_PRIMARY,
              border: `1px solid rgba(20,78,140,0.14)`,
            }}
          />
        </Stack>

        <Box sx={{ p: { xs: 1.5, md: 1.8 } }}>
          <Grid container spacing={1.2} sx={{ mb: 1.4 }}>
            <Grid item xs={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.35, md: 1.5 },
                  borderRadius: 2.5,
                  background: BG_PRIMARY,
                  border: `1px solid ${BLUE_TINT_18}`,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.75rem', lineHeight: 1.4, mb: 0.55 }}>
                  {t('highlightsSection.metrics.totalTeams')}
                </Typography>
                <Typography
                  sx={{
                    color: INK,
                    fontWeight: 900,
                    fontSize: { xs: '1.5rem', md: '1.7rem' },
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    lineHeight: 1,
                  }}
                >
                  {data.total_teams}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.35, md: 1.5 },
                  borderRadius: 2.5,
                  background: PINK_TINT_04,
                  border: `1px solid ${PINK_TINT_18}`,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.75rem', lineHeight: 1.4, mb: 0.55 }}>
                  {t('highlightsSection.metrics.advancingTeams')}
                </Typography>
                <Typography
                  sx={{
                    color: ACCENT_PINK,
                    fontWeight: 900,
                    fontSize: { xs: '1.5rem', md: '1.7rem' },
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    lineHeight: 1,
                  }}
                >
                  {data.advancing_count}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${HAIRLINE}`,
              overflow: 'hidden',
              background: BG_PRIMARY,
            }}
          >
            {rows.length === 0 ? (
              <Typography sx={{ color: TEXT_DIM, p: 2, fontSize: '0.92rem', textAlign: 'center' }}>
                {t('highlightsSection.pending')}
              </Typography>
            ) : (
              rows.map((team, index) => {
                const isLeader = index === 0
                const accent = isLeader ? ACCENT_PINK : SOLID_BLUE

                return (
                  <Box
                    key={`${team.team_name}_${index}`}
                    sx={{
                      p: { xs: 1.35, md: 1.55 },
                      background: isLeader ? PINK_TINT_04 : BG_PRIMARY,
                      borderTop: index === 0 ? 'none' : `1px solid ${HAIRLINE}`,
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: isLeader ? PINK_TINT_10 : BLUE_TINT_04,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '44px minmax(0, 1fr)', sm: '58px minmax(0, 1fr) auto' },
                        gap: { xs: 1.1, sm: 1.35 },
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 38, sm: 44 },
                          height: { xs: 38, sm: 44 },
                          borderRadius: '50%',
                          background: isLeader ? ACCENT_PINK : BLUE_TINT_08,
                          color: isLeader ? 'common.white' : SOLID_BLUE,
                          border: `1px solid ${isLeader ? ACCENT_PINK : BLUE_TINT_18}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontWeight: 900,
                          fontSize: '0.84rem',
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: INK, fontWeight: 900, fontSize: '0.98rem', lineHeight: 1.35 }}>
                          {team.team_name}
                        </Typography>
                        <Typography sx={{ color: TEXT_DIM, fontSize: '0.82rem', lineHeight: 1.5 }}>
                          {team.university || '—'}
                        </Typography>
                      </Box>

                      <Chip
                        label={getCompetitionStatusLabel(data.round?.round_type, team.competition_status, t)}
                        size="small"
                        sx={{
                          gridColumn: { xs: '2 / 3', sm: 'auto' },
                          justifySelf: { xs: 'flex-start', sm: 'flex-end' },
                          fontWeight: 800,
                          color: accent,
                          background: isLeader ? BG_PRIMARY : BLUE_TINT_04,
                          border: `1px solid ${isLeader ? PINK_TINT_18 : BLUE_TINT_18}`,
                        }}
                      />
                    </Box>
                  </Box>
                )
              })
            )}
          </Paper>
        </Box>
      </Stack>
    </Paper>
  )
}

function ResultsSection() {
  const { t } = useTranslation()
  const { data: cases = [], isLoading, isError } = useGetPublicResultsCasesQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const summaryPoints = t('highlightsSection.summaryPoints', { returnObjects: true }) as string[]
  const progressUpdates = t('highlightsSection.progressUpdates', { returnObjects: true }) as string[]
  const notices = t('highlightsSection.notices', { returnObjects: true }) as string[]
  const [resultsRef, resultsEntered] = useOnceInView<HTMLDivElement>()

  return (
    <Box
      ref={resultsRef}
      id="highlights"
      sx={{
        pt: { xs: 10, md: 14 },
        pb: { xs: 10, md: 14 },
        background: templateNavySection,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SectionWatermark
        number="06"
        side="center"
        dark
        top={{ xs: 36 + WATERMARK_OFFSET_2CM, md: 44 + WATERMARK_OFFSET_2CM }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -1,
          left: 0,
          right: 0,
          lineHeight: 0,
          zIndex: 0,
          '& svg': { width: '100%', height: { xs: 42, md: 70 }, display: 'block' },
        }}
      >
        <svg viewBox="0 0 1440 70" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,0 L0,0 Z" fill={BG_PINK} />
        </svg>
      </Box>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          lineHeight: 0,
          zIndex: 0,
          transform: 'rotate(180deg)',
          '& svg': { width: '100%', height: { xs: 42, md: 70 }, display: 'block' },
        }}
      >
        <svg viewBox="0 0 1440 70" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,0 L0,0 Z" fill={BG_PINK} />
        </svg>
      </Box>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 3.5, md: 5 } }}>
          <Eyebrow>{t('highlightsSection.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: 'common.white',
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '1.9rem', md: '3rem' },
              lineHeight: 1.12,
              maxWidth: 760,
            }}
          >
            {t('highlightsSection.title')}
          </Typography>
          <Box sx={{ width: 64, height: 3, background: TEMPLATE_PINK, borderRadius: 1 }} />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.82)',
              maxWidth: 820,
              fontSize: '1rem',
              lineHeight: 1.75,
            }}
          >
            {t('highlightsSection.subtitle')}
          </Typography>
        </Stack>

        <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="stretch">
          <Grid item xs={12} lg={8}>
            <Stack spacing={{ xs: 2, md: 2.35 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.35 },
                  background: 'rgba(255,255,255,0.08)',
                  border: '2.5px solid rgba(255,255,255,0.72)',
                  backdropFilter: 'blur(6px)',
                  borderRadius: 3.5,
                  opacity: resultsEntered ? 1 : 0,
                  animation: resultsEntered ? 'resultsPanelIn 1400ms cubic-bezier(0.2, 0.8, 0.2, 1) 250ms both' : 'none',
                  '@keyframes resultsPanelIn': {
                    '0%': { opacity: 0, transform: 'translateY(28px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Stack spacing={1.4} alignItems="center" textAlign="center">
                  <Typography sx={{ color: 'common.white', fontWeight: 900, fontSize: '1.04rem' }}>
                    {t('highlightsSection.summaryTitle')}
                  </Typography>
                  <Grid container spacing={1.15}>
                    {summaryPoints.map((point, index) => {
                      const pointAccent = index % 2 === 0 ? ACCENT_PINK : SOLID_BLUE
                      const pointBg = index % 2 === 0 ? 'rgba(255,247,251,0.96)' : 'rgba(240,246,253,0.96)'

                      return (
                      <Grid item xs={12} sm={6} key={point}>
                        <Paper
                          elevation={0}
                          sx={{
                            height: '100%',
                            p: 1.35,
                            borderRadius: 2.5,
                            background: pointBg,
                            border: `1px solid ${pointAccent}30`,
                            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                          }}
                        >
                          <Stack direction="row" spacing={0.9} alignItems="flex-start">
                            <CheckCircle
                              sx={{
                                color: pointAccent,
                                fontSize: 18,
                                mt: 0.18,
                                flexShrink: 0,
                              }}
                            />
                            <Typography sx={{ color: TEXT_BODY, fontSize: '0.9rem', lineHeight: 1.62, textAlign: 'left' }}>
                              {point}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                      )
                    })}
                  </Grid>
                </Stack>
              </Paper>

              {isLoading ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 3.5,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={32} />
                </Paper>
              ) : isError || cases.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px dashed rgba(255,255,255,0.22)',
                    borderRadius: 3.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ color: 'common.white', fontWeight: 700, mb: 1 }}>
                    {t('highlightsSection.empty')}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.76)', fontSize: '0.92rem' }}>
                    {t('highlightsSection.emptyBody')}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={2}>
                  {cases.map((c) => (
                    <ResultsCaseCard key={c.case_id} caseId={c.case_id} title={c.title} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={{ xs: 2, md: 2.35 }} sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.7, md: 1.9 },
                  background: 'linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 100%)',
                  border: '1.5px solid rgba(255,255,255,0.78)',
                  borderRadius: 3.5,
                  boxShadow: '0 22px 52px rgba(0,0,0,0.3)',
                  opacity: resultsEntered ? 1 : 0,
                  animation: resultsEntered ? 'resultsPanelIn 1400ms cubic-bezier(0.2, 0.8, 0.2, 1) 460ms both' : 'none',
                }}
              >
                <Stack spacing={1.25} alignItems="stretch">
                  <Stack direction="row" spacing={1.1} alignItems="center">
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        background: PINK_TINT_10,
                        color: ACCENT_PINK,
                        border: `1px solid ${PINK_TINT_18}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Visibility sx={{ fontSize: 24 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: INK, fontWeight: 900, fontSize: '1.02rem', lineHeight: 1.2 }}>
                        {t('highlightsSection.noticesTitle')}
                      </Typography>
                      <Box sx={{ width: 48, mt: 0.8, borderTop: `2px solid ${ACCENT_PINK}` }} />
                    </Box>
                  </Stack>
                  <Stack spacing={0.95} sx={{ width: '100%' }}>
                    {notices.map((notice, index) => {
                      const noticeAccent = index % 2 === 0 ? ACCENT_PINK : SOLID_BLUE
                      const noticeBg = index % 2 === 0 ? '#FFF7FB' : '#F4F8FE'

                      return (
                      <Paper
                        key={notice}
                        elevation={0}
                        sx={{
                          p: { xs: 1.15, md: 1.25 },
                          borderRadius: 2.4,
                          background: noticeBg,
                          border: `1px solid ${noticeAccent}28`,
                          borderLeft: `2px solid ${noticeAccent}`,
                          textAlign: 'left',
                          boxShadow: '0 8px 18px rgba(20,78,140,0.08)',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            borderColor: noticeAccent,
                            boxShadow: '0 10px 22px rgba(20,78,140,0.12)',
                          },
                        }}
                      >
                        <Stack direction="row" spacing={0.85} alignItems="flex-start">
                          <CheckCircle sx={{ color: noticeAccent, fontSize: 18, mt: 0.16, flexShrink: 0 }} />
                          <Typography sx={{ color: TEXT_BODY, fontSize: '0.88rem', lineHeight: 1.5, fontWeight: 700 }}>
                            {notice}
                          </Typography>
                        </Stack>
                      </Paper>
                      )
                    })}
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.7, md: 1.9 },
                  background: 'linear-gradient(160deg, #FFFFFF 0%, #FFF7FB 100%)',
                  border: '1.5px solid rgba(246,65,131,0.38)',
                  borderRadius: 3.5,
                  boxShadow: '0 22px 52px rgba(0,0,0,0.3)',
                  alignSelf: 'flex-start',
                  width: '100%',
                  opacity: resultsEntered ? 1 : 0,
                  animation: resultsEntered ? 'resultsPanelIn 1400ms cubic-bezier(0.2, 0.8, 0.2, 1) 670ms both' : 'none',
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1.1} alignItems="center">
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        background: BLUE_TINT_08,
                        color: SOLID_BLUE,
                        border: `1px solid ${BLUE_TINT_18}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Schedule sx={{ fontSize: 23 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: INK, fontWeight: 900, fontSize: '1.02rem', lineHeight: 1.2 }}>
                        {t('highlightsSection.progressTitle')}
                      </Typography>
                      <Box sx={{ width: 48, mt: 0.8, borderTop: `2px solid ${SOLID_BLUE}` }} />
                    </Box>
                  </Stack>
                  <Stack spacing={1.1}>
                    {progressUpdates.map((update, index) => {
                      const accent = index % 2 === 0 ? SOLID_BLUE : ACCENT_PINK
                      const panel = index % 2 === 0 ? '#F3F8FE' : '#FFF4F8'

                      return (
                        <Paper
                          key={update}
                          elevation={0}
                          sx={{
                            p: { xs: 1.1, md: 1.22 },
                            borderRadius: 2.4,
                            background: panel,
                            border: `1px solid ${accent}28`,
                            borderLeft: `2px solid ${accent}`,
                            boxShadow: '0 8px 18px rgba(20,78,140,0.08)',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                              borderColor: accent,
                              boxShadow: '0 10px 22px rgba(20,78,140,0.12)',
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 1.6,
                                background: accent,
                                color: 'common.white',
                                border: `1px solid ${accent}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontSize: '0.74rem',
                                fontWeight: 900,
                              }}
                            >
                              {String(index + 1).padStart(2, '0')}
                            </Box>
                            <Typography sx={{ color: TEXT_BODY, fontSize: '0.88rem', lineHeight: 1.62, fontWeight: 600 }}>
                              {update}
                            </Typography>
                          </Stack>
                        </Paper>
                      )
                    })}
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Rules — white bg, 2-column cards
// ═══════════════════════════════════════════════════════════════════════
function RulesSection() {
  const { t } = useTranslation()
  const fileHref = t('rules.fileHref')
  const rulesTitle = t('rules.title')
  const rulesTitleHighlight = rulesTitle.match(/cuộc thi$/i)?.[0]
  const rulesTitleBase = rulesTitleHighlight ? rulesTitle.replace(/\s+cuộc thi$/i, '') : rulesTitle
  const documentAccent = timelineAccents[0]
  const [rulesRef, rulesEntered] = useOnceInView<HTMLDivElement>()
  const [pdfShakeRun, setPdfShakeRun] = useState(0)
  const [pdfShakeDelay, setPdfShakeDelay] = useState(0)

  useEffect(() => {
    if (rulesEntered) {
      setPdfShakeDelay(250)
      setPdfShakeRun((current) => current + 1)
    }
  }, [rulesEntered])

  return (
    <Box
      ref={rulesRef}
      id="rules"
      sx={{
        pt: { xs: 5.5, md: 7 },
        pb: { xs: 8, md: 12 },
        background: BG_PRIMARY,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SectionWatermark number="03" side="left" />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 86% 10%, rgba(217,53,111,0.06), transparent 16%),
            radial-gradient(circle at 94% 18%, rgba(20,78,140,0.05), transparent 18%),
            radial-gradient(circle at 12% 76%, rgba(20,78,140,0.03), transparent 18%)
          `,
          pointerEvents: 'none',
        }}
      />

      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 120,
          right: '7%',
          width: 92,
          height: 92,
          opacity: 0.28,
          backgroundImage: `radial-gradient(${BLUE_TINT_18} 1.4px, transparent 1.4px)`,
          backgroundSize: '18px 18px',
          pointerEvents: 'none',
        }}
      />

      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 360,
          right: '4.5%',
          width: 84,
          height: 84,
          opacity: 0.24,
          backgroundImage: `radial-gradient(${PINK_TINT_18} 1.4px, transparent 1.4px)`,
          backgroundSize: '16px 16px',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 3.5, md: 5 }} alignItems="center">
          <Grid item xs={12} lg={7}>
            <Stack spacing={2.2}>
              <Eyebrow>{t('rules.eyebrow')}</Eyebrow>
              <Typography
                component="h2"
                sx={{
                  color: INK,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: -0.5,
                  fontSize: { xs: '2.65rem', md: '5rem' },
                  lineHeight: 0.98,
                  maxWidth: 680,
                }}
              >
                {rulesTitleBase}
                {rulesTitleHighlight ? (
                  <Box component="span" sx={{ display: 'block', color: TEMPLATE_CORAL, whiteSpace: 'nowrap' }}>
                    {rulesTitleHighlight}
                  </Box>
                ) : null}
              </Typography>
              <Typography
                sx={{
                  color: TEXT_BODY,
                  fontSize: { xs: '1rem', md: '1.08rem' },
                  lineHeight: 1.78,
                  maxWidth: 680,
                }}
              >
                {t('rules.subtitle')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                background: BG_PRIMARY,
                border: `1px solid ${documentAccent.border}`,
                borderRadius: 3.5,
                overflow: 'hidden',
                boxShadow: CARD_SHADOW,
                display: 'flex',
                flexDirection: 'column',
                transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
                '&:hover, &:focus-within': {
                  borderColor: documentAccent.accent,
                  boxShadow: CARD_SHADOW_HOVER,
                },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: { xs: 280, md: 360 },
                  p: { xs: 3, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 3, md: 3.5 },
                  background:
                    'radial-gradient(circle at 50% 34%, rgba(20,78,140,0.08), transparent 28%), linear-gradient(180deg, #FFFFFF, #F7FAFD)',
                }}
              >
                <Box
                  key={`rules-pdf-${pdfShakeRun}`}
                  component="img"
                  src="/pdf2.png"
                  alt={t('rules.fileFormat')}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setPdfShakeDelay(0)
                    setPdfShakeRun((current) => current + 1)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setPdfShakeDelay(0)
                      setPdfShakeRun((current) => current + 1)
                    }
                  }}
                  sx={{
                    width: { xs: 118, md: 148 },
                    height: 'auto',
                    display: 'block',
                    cursor: 'pointer',
                    animation: pdfShakeRun > 0 ? `pdfShake 960ms ease-in-out ${pdfShakeDelay}ms 2 both` : 'none',
                    transformOrigin: '50% 58%',
                    '@keyframes pdfShake': {
                      '0%, 100%': { transform: 'rotate(0deg) scale(1)' },
                      '12%': { transform: 'rotate(-8deg) scale(1.035)' },
                      '24%': { transform: 'rotate(7deg) scale(1.03)' },
                      '36%': { transform: 'rotate(-6deg) scale(1.025)' },
                      '48%': { transform: 'rotate(5deg) scale(1.02)' },
                      '60%': { transform: 'rotate(-4deg) scale(1.012)' },
                      '72%': { transform: 'rotate(3deg) scale(1.008)' },
                      '84%': { transform: 'rotate(-2deg) scale(1.004)' },
                    },
                  }}
                />
                <Stack spacing={0.9} alignItems="center" textAlign="center" sx={{ mt: -1.5 }}>
                  <Typography
                    sx={{
                      color: documentAccent.accent,
                      fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                      fontSize: { xs: '1.5rem', md: '1.5rem' },
                      fontWeight: 900,
                      letterSpacing: 0.4,
                    }}
                  >
                    {t('rules.fileName')}
                  </Typography>
                  <Typography
                    sx={{
                      color: documentAccent.accent,
                      fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                      fontSize: { xs: '0.86rem', md: '0.92rem' },
                      fontWeight: 900,
                      letterSpacing: 0.4,
                    }}
                  >
                    {t('rules.fileMeta')}
                  </Typography>
                  <Typography
                    sx={{
                      color: TEXT_BODY,
                      fontSize: { xs: '0.92rem', md: '0.98rem' },
                      lineHeight: 1.58,
                      maxWidth: 340,
                    }}
                  >
                    {t('rules.fileDescription')}
                  </Typography>
                </Stack>
                <Button
                  component="a"
                  href={fileHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  variant="contained"
                  endIcon={<OpenInNew fontSize="small" />}
                  sx={{
                    maxWidth: 300,
                    minHeight: 56,
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 999,
                    backgroundColor: SOLID_BLUE,
                    boxShadow: '0 14px 32px rgba(20,78,140,0.18)',
                    '&:hover': {
                      backgroundColor: SOLID_BLUE,
                      boxShadow: '0 16px 36px rgba(20,78,140,0.24)',
                    },
                  }}
                >
                  {t('rules.cta')}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Awards — light pink section (feminist accent)
// ═══════════════════════════════════════════════════════════════════════
function AwardsSection() {
  const { t } = useTranslation()
  const teamAwards = t('awards.teamAwards', { returnObjects: true }) as Array<{
    rank: string
    amount: string
    description?: string
  }>
  const summaryItems = t('awards.summary', { returnObjects: true }) as Array<{
    label: string
    value: string
  }>
  const specialAwards = t('awards.specialAwards', { returnObjects: true }) as Array<{
    title: string
    amount: string
    type: 'oral' | 'document'
  }>
  const opportunities = t('awards.opportunities', { returnObjects: true }) as string[]
  const opportunityIcons = [Gavel, Diversity3, Groups]
  const awardStages = ['01', '02', '03']
  const mainAwardOrder = [
    { awardIndex: 1, desktopOrder: 1, mobileOrder: 2 },
    { awardIndex: 0, desktopOrder: 2, mobileOrder: 1 },
    { awardIndex: 2, desktopOrder: 3, mobileOrder: 3 },
  ]
  const flatAwardAccents = [
    { ...awardAccents[0], surface: '#FFF8EA', panel: 'rgba(201,154,46,0.12)', border: 'rgba(201,154,46,0.28)' },
    { ...awardAccents[1], surface: BLUE_TINT_04, panel: BLUE_TINT_08, border: BLUE_TINT_18, icon: SOLID_BLUE },
    { ...awardAccents[2], surface: PINK_TINT_04, panel: PINK_TINT_10, border: PINK_TINT_18, icon: ACCENT_PINK },
  ]
  const [awardsRef, awardsRunKey] = useReplayInView<HTMLDivElement>()

  return (
    <Box
      ref={awardsRef}
      id="awards"
      sx={{
        py: { xs: 8, md: 12 },
        background: BG_PINK,
        position: 'relative',
        overflow: 'hidden',
        ...sectionTexture,
      }}
    >
      <SectionWatermark number="04" side="center" top={{ xs: 20, md: 24 }} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 3.5, md: 4.5 } }}>
          <Eyebrow>{t('awards.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: INK,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '2.25rem', md: '3.35rem' },
              lineHeight: 1.06,
              maxWidth: 920,
            }}
          >
            {t('awards.title')}
          </Typography>
          <Box sx={{ width: 64, height: 3, background: gradientBrand, borderRadius: 1 }} />
          <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
            {t('awards.subtitle')}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.4}
          justifyContent="center"
          alignItems="stretch"
          sx={{ mb: { xs: 3, md: 4 } }}
        >
          {summaryItems.map((item, index) => (
            <Paper
              key={`${item.label}-${awardsRunKey}`}
              elevation={0}
              sx={{
                px: { xs: 2, md: 2.4 },
                py: 1.45,
                background: BG_PRIMARY,
                border: `1px solid ${index === 0 ? PINK_TINT_18 : BLUE_TINT_18}`,
                borderRadius: 999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.2,
                minWidth: { xs: '100%', sm: 220 },
                opacity: awardsRunKey > 0 ? 1 : 0,
                animation: awardsRunKey > 0
                  ? `awardSummaryIn 1200ms cubic-bezier(0.2, 0.8, 0.2, 1) ${250 + index * 120}ms both`
                  : 'none',
                '@keyframes awardSummaryIn': {
                  '0%': { opacity: 0, transform: 'translateY(18px) scale(0.98)' },
                  '62%': { opacity: 1, transform: 'translateY(0) scale(1.012)' },
                  '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
                },
                transition: 'border-color 0.22s ease, background-color 0.22s ease',
                '&:hover': {
                  borderColor: index === 0 ? ACCENT_PINK : SOLID_BLUE,
                  backgroundColor: '#FFFDFE',
                },
              }}
            >
              <Typography
                sx={{
                  color: index === 0 ? ACCENT_PINK : SOLID_BLUE,
                  fontWeight: 900,
                  fontSize: '1.45rem',
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  lineHeight: 1,
                }}
              >
                {item.value}
              </Typography>
              <Typography sx={{ color: INK, fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.3 }}>
                {item.label}
              </Typography>
            </Paper>
          ))}
        </Stack>

        <Stack spacing={{ xs: 2, md: 2.5 }}>
          <Grid container spacing={{ xs: 2, md: 2.4 }} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
            {mainAwardOrder.map((entry) => {
              const item = teamAwards[entry.awardIndex]
              if (!item) return null

              const accent = flatAwardAccents[entry.awardIndex % flatAwardAccents.length]
              const featured = entry.awardIndex === 0

              return (
                <Grid item xs={12} md={4} key={`${item.rank}-${awardsRunKey}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      order: { xs: entry.mobileOrder, md: entry.desktopOrder },
                      minHeight: { xs: featured ? 286 : 260, md: featured ? 370 : 320 },
                      height: '100%',
                      p: { xs: 2.6, md: 3.1 },
                      background: featured ? accent.surface : BG_PRIMARY,
                      border: `1px solid ${accent.border}`,
                      borderRadius: 3.5,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: { xs: 1.6, md: 1.8 },
                      boxShadow: featured ? '0 18px 44px rgba(201,154,46,0.16)' : CARD_SHADOW,
                      opacity: awardsRunKey > 0 ? 1 : 0,
                      animation: awardsRunKey > 0
                        ? `awardCardIn 1500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${380 + entry.desktopOrder * 155}ms both`
                        : 'none',
                      '@keyframes awardCardIn': {
                        '0%': { opacity: 0, transform: 'translateY(30px)' },
                        '70%': { opacity: 1, transform: 'translateY(-4px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' },
                      },
                      transition: 'border-color 0.22s ease, background-color 0.22s ease, box-shadow 0.22s ease',
                      '&:hover': {
                        borderColor: accent.icon,
                        backgroundColor: featured ? accent.surface : accent.panel,
                        boxShadow: CARD_SHADOW_HOVER,
                      },
                    }}
                  >
                    <Stack spacing={1.15} alignItems="center">
                      <Box
                        sx={{
                          width: { xs: 68, md: 78 },
                          height: { xs: 68, md: 78 },
                          borderRadius: '50%',
                          background: featured ? BG_PRIMARY : accent.panel,
                          color: accent.icon,
                          border: `1px solid ${accent.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EmojiEvents sx={{ fontSize: { xs: 38, md: 44 } }} />
                      </Box>
                      <Typography
                        sx={{
                          color: accent.icon,
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontSize: '0.75rem',
                          letterSpacing: 1.5,
                          fontWeight: 900,
                        }}
                      >
                        {awardStages[entry.awardIndex]}
                      </Typography>
                    </Stack>

                    <Box>
                      <Typography
                        sx={{
                          color: INK,
                          fontWeight: 900,
                          fontSize: { xs: '1.45rem', md: '1.62rem' },
                          lineHeight: 1.18,
                          mb: 1,
                        }}
                      >
                        {item.rank}
                      </Typography>
                      <Typography
                        sx={{
                          color: accent.icon,
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontSize: { xs: '1.42rem', md: '1.62rem' },
                          lineHeight: 1.2,
                          fontWeight: 900,
                          mb: 0.85,
                        }}
                      >
                        <AnimatedAmount value={item.amount} runKey={awardsRunKey} />
                      </Typography>
                      {item.description ? (
                        <Typography sx={{ color: TEXT_BODY, fontSize: '0.92rem', lineHeight: 1.6 }}>
                          {item.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>

          <Grid container spacing={{ xs: 1.5, md: 1.8 }} alignItems="stretch" justifyContent="center">
            {specialAwards.map((item) => {
              const accent = item.type === 'oral' ? SOLID_BLUE : ACCENT_PINK
              const panel = item.type === 'oral' ? BLUE_TINT_04 : PINK_TINT_04
              const SpecialAwardIcon = item.type === 'oral' ? Gavel : Article

              return (
                <Grid item xs={12} sm={6} md={5} key={`${item.title}-${awardsRunKey}`}>
                  <Paper
                    elevation={0}
	                    sx={{
	                      height: '100%',
	                      minHeight: { xs: 92, md: 104 },
	                      p: { xs: 1.05, md: 1.15 },
                      background: BG_PRIMARY,
                      border: `1px solid ${accent}20`,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: awardsRunKey > 0 ? 1 : 0,
                      animation: awardsRunKey > 0
                        ? `awardCardIn 1350ms cubic-bezier(0.2, 0.8, 0.2, 1) 760ms both`
                        : 'none',
                      transition: 'border-color 0.22s ease, background-color 0.22s ease, box-shadow 0.22s ease',
                      '&:hover': {
                        borderColor: accent,
                        backgroundColor: panel,
                        boxShadow: CARD_SHADOW_HOVER,
                      },
                    }}
                  >
	                    <Stack
                        direction="column"
                        spacing={0.55}
                        alignItems="center"
                        justifyContent="center"
                        textAlign={{ xs: 'center', sm: 'center' }}
                        sx={{ width: '100%' }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            background: panel,
                            color: accent,
                            border: `1px solid ${accent}24`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mb: 0.05,
                          }}
                        >
                          <SpecialAwardIcon sx={{ fontSize: 19 }} />
                        </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
	                        <Typography sx={{ color: accent, fontWeight: 900, fontSize: '0.72rem', letterSpacing: 0.9, mb: 0.3 }}>
	                          {t('awards.specialAwardsTitle')}
	                        </Typography>
	                        <Typography sx={{ color: INK, fontWeight: 900, fontSize: { xs: '1.06rem', md: '1.12rem' }, lineHeight: 1.18, mb: 0.3 }}>
	                          {item.title}
	                        </Typography>
	                        <Typography sx={{ color: TEXT_BODY, fontSize: { xs: '0.92rem', md: '1rem' }, lineHeight: 1.28, fontWeight: 750 }}>
	                          <AnimatedAmount value={item.amount} runKey={awardsRunKey} />
	                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>

          <Grid container spacing={{ xs: 2, md: 2.4 }} alignItems="stretch">
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
	                sx={{
	                  height: '100%',
	                  minHeight: { xs: 228, md: 236 },
	                  p: { xs: 2, md: 2.25 },
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 3.5,
                  transition: 'border-color 0.22s ease, background-color 0.22s ease, box-shadow 0.22s ease',
                  '&:hover': {
                    borderColor: SOLID_BLUE,
                    backgroundColor: '#FFFDFE',
                    boxShadow: CARD_SHADOW_HOVER,
                  },
                }}
              >
	                <Stack spacing={1.5} sx={{ height: '100%' }}>
                  <Typography sx={{ color: INK, fontWeight: 900, fontSize: { xs: '1.45rem', md: '1.78rem' }, lineHeight: 1.08 }}>
                    {t('awards.opportunitiesTitle')}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                      gridAutoRows: 'auto',
	                      gap: 1.15,
                      alignItems: 'stretch',
                    }}
                  >
                    {opportunities.map((item, index) => {
                      const accent = index % 2 === 0 ? SOLID_BLUE : ACCENT_PINK
                      const panel = index % 2 === 0 ? BLUE_TINT_04 : PINK_TINT_04
                      const OpportunityIcon = opportunityIcons[index % opportunityIcons.length]

                      return (
                        <Box key={item} sx={{ gridColumn: { xs: 'auto', sm: index === 0 ? 'span 2' : 'auto' } }}>
                          <Paper
                            elevation={0}
	                            sx={{
	                              height: '100%',
	                              minHeight: { xs: 86, md: 92 },
	                              p: { xs: 1.25, md: 1.35 },
                              borderRadius: 2.5,
                              background: panel,
                              border: `1px solid ${accent}18`,
                              display: 'flex',
                              alignItems: 'center',
                              transition: 'border-color 0.22s ease, background-color 0.22s ease',
                              '&:hover': {
                                borderColor: accent,
                                backgroundColor: index % 2 === 0 ? BLUE_TINT_08 : PINK_TINT_10,
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                              textAlign="left"
                              sx={{ width: '100%' }}
                            >
                              <Box
                                sx={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: 1.8,
                                  background: BG_PRIMARY,
                                  color: accent,
                                  border: `1px solid ${accent}14`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <OpportunityIcon sx={{ fontSize: 23 }} />
                              </Box>
	                              <Typography
                                  sx={{
                                    color: TEXT_BODY,
                                    fontSize: { xs: '0.9rem', md: '0.96rem' },
                                    lineHeight: 1.36,
                                    textWrap: 'pretty',
                                  }}
                                >
	                                {item}
	                              </Typography>
                            </Stack>
                          </Paper>
                        </Box>
                      )
                    })}
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
	                sx={{
	                  height: '100%',
	                  minHeight: { xs: 172, md: 236 },
	                  p: { xs: 2, md: 2.25 },
                  borderRadius: 3.5,
                  background: 'linear-gradient(160deg, #FFF7FB, #FFFFFF 62%)',
                  border: `1.5px solid ${ACCENT_PINK}`,
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 18px 38px rgba(246,65,131,0.14)',
                  transition: 'border-color 0.22s ease, background-color 0.22s ease, box-shadow 0.22s ease',
                  '&:hover': {
                    borderColor: ACCENT_PINK,
                    backgroundColor: PINK_TINT_04,
                    boxShadow: CARD_SHADOW_HOVER,
                  },
                }}
              >
	                <Stack spacing={1.15} alignItems="center" textAlign="center">
                  <Box
                    sx={{
	                      width: 54,
	                      height: 54,
                      borderRadius: 2.3,
                      background: ACCENT_PINK,
                      color: 'common.white',
                      border: `1px solid ${ACCENT_PINK}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
	                    <WorkspacePremium sx={{ fontSize: 30 }} />
                  </Box>
	                  <Typography sx={{ color: INK, fontWeight: 900, fontSize: { xs: '1.36rem', md: '1.58rem' }, lineHeight: 1.1 }}>
	                    {t('awards.certificateTitle')}
	                  </Typography>
	                  <Typography sx={{ color: TEXT_BODY, fontSize: '0.84rem', lineHeight: 1.55 }}>
	                    {t('awards.certificateBody')}
	                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Competition Moments
// ═══════════════════════════════════════════════════════════════════════
function PublicTeamsSection() {
  const { t } = useTranslation()
  const tags = t('moments.tags', { returnObjects: true }) as string[]

  return (
    <Box id="moments" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('moments.eyebrow')}
          title={t('moments.title')}
          subtitle={t('moments.subtitle')}
        />

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${SOLID_BLUE}`,
                background: BG_SOFT,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Typography
                sx={{
                  color: INK,
                  fontWeight: 800,
                  fontSize: { xs: '1.3rem', md: '1.7rem' },
                  lineHeight: 1.4,
                  mb: 2,
                }}
              >
                {t('moments.statement')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    sx={{
                      fontWeight: 700,
                      color: INK,
                      background: BG_PRIMARY,
                      border: `1px solid ${HAIRLINE}`,
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${ACCENT_PINK}`,
                background: PINK_TINT_04,
                boxShadow: CARD_SHADOW,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box>
                <Visibility sx={{ color: brand.hotPink, fontSize: 34, mb: 1.5 }} />
                <Typography
                  sx={{
                    color: INK,
                    fontStyle: 'italic',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.8,
                    mb: 1.5,
                  }}
                >
                  &ldquo;{t('moments.quote')}&rdquo;
                </Typography>
                <Typography
                  sx={{
                    color: TEXT_DIM,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    letterSpacing: 0.4,
                  }}
                >
                  {t('moments.author')}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Partners — white bg, logo placeholder tiles
// ═══════════════════════════════════════════════════════════════════════
function PartnersSection() {
  return null
}

// ═══════════════════════════════════════════════════════════════════════
// Contact — off-white section
// ═══════════════════════════════════════════════════════════════════════
function ContactSection({ resultsVisible }: { resultsVisible: boolean }) {
  const { t } = useTranslation()
  const registerHref =
    siteContent.registrationFormUrl ||
    'https://docs.google.com/forms/d/e/1FAIpQLSe7AXGzOp0rrxkAx1ipx3_fq0M0IFRj_i1BSClaQomLHPwlow/viewform'
  const channels = t('contact.channels', { returnObjects: true }) as Array<{
    label: string
    value: string
    href?: string
    kind: 'email' | 'fanpage' | 'website' | 'phone'
  }>
  const quickAccess = t('contact.quickAccess', { returnObjects: true }) as Array<{
    label: string
    action: 'register' | 'rules' | 'contact'
  }>
  const visibleChannels = channels.filter((channel) => channel.kind !== 'phone')
  const orderedChannels = ['email', 'website', 'fanpage']
    .map((kind) => visibleChannels.find((channel) => channel.kind === kind))
    .filter(Boolean) as typeof visibleChannels
  const websiteHref = visibleChannels.find((channel) => channel.kind === 'website')?.href || '#contact'

  const getChannelIcon = (kind: string) => {
    switch (kind) {
      case 'fanpage':
        return <Facebook />
      case 'website':
        return <PublicIcon />
      case 'phone':
        return <Schedule />
      case 'email':
      default:
        return <Mail />
    }
  }

  const getChannelAccent = (kind: string) => {
    switch (kind) {
      case 'website':
        return TEMPLATE_PINK
      case 'fanpage':
      case 'email':
        return SOLID_BLUE
      default:
        return TEMPLATE_CORAL
    }
  }

  const getQuickAccessHref = (action: string) => {
    switch (action) {
      case 'register':
        return registerHref
      case 'rules':
        return '#rules'
      case 'contact':
      default:
        return visibleChannels.find((channel) => channel.kind === 'email')?.href || '#contact'
    }
  }
  const [contactRef, contactEntered] = useOnceInView<HTMLDivElement>()

  return (
    <Box
      ref={contactRef}
      id="contact"
      sx={{
        pt: { xs: 10, md: 14 },
        pb: { xs: 8, md: 12 },
        background: templateNavySection,
        color: 'common.white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SectionWatermark
        number={resultsVisible ? '09' : '08'}
        side="left"
        dark
        top={resultsVisible ? { xs: 18, md: 24 } : { xs: 18 + WATERMARK_OFFSET_1CM, md: 24 + WATERMARK_OFFSET_1CM }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -1,
          left: 0,
          right: 0,
          lineHeight: 0,
          zIndex: 0,
          '& svg': { width: '100%', height: { xs: 42, md: 70 }, display: 'block' },
        }}
      >
        <svg viewBox="0 0 1440 70" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,0 L0,0 Z" fill={BG_PRIMARY} />
        </svg>
      </Box>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} sx={{ mb: { xs: 3.5, md: 5 } }}>
          <Eyebrow dark>{t('contact.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: 'common.white',
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '1.9rem', md: '3rem' },
              lineHeight: 1.12,
            }}
          >
            {t('contact.title')}
          </Typography>
          <Box sx={{ width: 64, height: 3, background: TEMPLATE_PINK, borderRadius: 1 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.82)', maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
            {t('contact.subtitle')}
          </Typography>
        </Stack>

        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.2, md: 2.55 },
                height: '100%',
                background: 'linear-gradient(180deg, #FFFFFF, #F6F8FC)',
                border: `1px solid ${BLUE_TINT_18}`,
                borderRadius: 3,
                boxShadow: '0 22px 52px rgba(0,0,0,0.22)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack spacing={{ xs: 1.8, md: 2 }} sx={{ height: '100%' }}>
                <Box>
                  <Typography
                    sx={{
                      color: INK,
                      fontWeight: 900,
                      fontSize: { xs: '1.36rem', md: '1.72rem' },
                      lineHeight: 1.12,
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('contact.informationTitle')}
                  </Typography>
                  <Box sx={{ width: 84, mt: 1.05, borderTop: `2px solid ${ACCENT_PINK}` }} />
                </Box>

                <Stack spacing={0} sx={{ flex: 1, justifyContent: 'space-evenly', minHeight: 0 }}>
                  {orderedChannels.map((channel, index) => {
                    const isExternal = channel.kind === 'fanpage' || channel.kind === 'website'
                    const accent = getChannelAccent(channel.kind)

                    return (
                      <Box
                        key={channel.label}
                        component={channel.href ? 'a' : 'div'}
                        href={channel.href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        sx={{
                          py: { xs: 1.75, md: 2.15 },
                          display: 'grid',
                          gridTemplateColumns: { xs: '42px minmax(0, 1fr)', sm: '48px 150px minmax(0, 1fr) 22px' },
                          gap: { xs: 1.2, sm: 1.4 },
                          alignItems: 'center',
                          border: `1px solid ${accent}36`,
                          px: { xs: 1.1, sm: 1.35 },
                          borderRadius: 2,
                          color: TEXT_BODY,
                          textDecoration: 'none',
                          background: index % 2 === 0 ? BG_PRIMARY : PINK_TINT_04,
                          opacity: contactEntered ? 1 : 0,
                          animation: contactEntered
                            ? `contactItemIn 1250ms cubic-bezier(0.2, 0.8, 0.2, 1) ${250 + index * 140}ms both`
                            : 'none',
                          '@keyframes contactItemIn': {
                            '0%': { opacity: 0, transform: 'translateY(22px)' },
                            '100%': { opacity: 1, transform: 'translateY(0)' },
                          },
                          transition: 'background-color 0.2s ease, border-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: index % 2 === 0 ? BLUE_TINT_04 : PINK_TINT_10,
                            borderColor: accent,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, md: 44 },
                            height: { xs: 40, md: 44 },
                            borderRadius: '50%',
                            color: accent,
                            border: `1px solid ${accent}40`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {getChannelIcon(channel.kind)}
                        </Box>

                        <Typography
                          sx={{
                            color: accent,
                            fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                            fontSize: { xs: '0.76rem', md: '0.8rem' },
                            letterSpacing: 1.2,
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            gridColumn: { xs: '2 / 3', sm: 'auto' },
                          }}
                        >
                          {channel.label}
                        </Typography>

                        <Typography
                          sx={{
                            color: TEXT_BODY,
                            fontSize: { xs: '0.94rem', md: '1rem' },
                            lineHeight: 1.55,
                            wordBreak: 'break-word',
                            gridColumn: { xs: '2 / 3', sm: 'auto' },
                          }}
                        >
                          {channel.value || channel.href || '—'}
                        </Typography>

                        {isExternal ? (
                          <OpenInNew sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 18, color: TEXT_DIM }} />
                        ) : (
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
                        )}
                      </Box>
                    )
                  })}
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.2 },
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.12)',
                border: '1.5px solid rgba(246,65,131,0.86)',
                backdropFilter: 'blur(6px)',
                borderRadius: 3,
                boxShadow: '0 24px 58px rgba(0,0,0,0.34)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.4,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: 'common.white',
                    fontWeight: 900,
                    fontSize: { xs: '1.08rem', md: '1.16rem' },
                    lineHeight: 1.28,
                  }}
                >
                  {t('contact.quickAccessTitle')}
                </Typography>
                <Box sx={{ width: 64, mt: 1.2, borderTop: `2px solid ${TEMPLATE_PINK}` }} />
              </Box>

              <Box
                component="a"
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  minHeight: { xs: 210, md: 250 },
                  border: '1px solid rgba(255,255,255,0.28)',
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                }}
              >
                <Box
                  component="img"
                  src="/icfgl-moot-court-qr.svg"
                  alt={t('contact.website')}
                  sx={{
                    width: { xs: 168, md: 196 },
                    height: { xs: 168, md: 196 },
                    display: 'block',
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  borderTop: 'none',
                  borderBottom: 'none',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                {quickAccess.map((item, index) => (
                  <Box
                    key={item.label}
                    component="a"
                    href={getQuickAccessHref(item.action)}
                    target={item.action === 'register' ? '_blank' : undefined}
                    rel={item.action === 'register' ? 'noopener noreferrer' : undefined}
                    sx={{
                      py: { xs: 0.75, sm: 0.85 },
                      px: { xs: 0.6, sm: 0.85 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: { xs: 0.45, sm: 0.7 },
                      color: index === 0 ? 'common.white' : SOLID_BLUE,
                      background: index === 0 ? TEMPLATE_PINK : BG_PRIMARY,
                      border: `1px solid ${index === 0 ? TEMPLATE_PINK : 'rgba(255,255,255,0.82)'}`,
                      borderRadius: 2,
                      boxShadow: index === 0 ? '0 12px 28px rgba(246,65,131,0.42)' : '0 12px 28px rgba(0,0,0,0.28)',
                      textDecoration: 'none',
                      minHeight: { xs: 48, sm: 52 },
                      minWidth: 0,
                      position: 'relative',
                      zIndex: 2,
                      opacity: contactEntered ? 1 : 0,
                      animation: contactEntered
                        ? `contactItemIn 1250ms cubic-bezier(0.2, 0.8, 0.2, 1) ${520 + index * 145}ms both`
                        : 'none',
                      transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
                      '&:hover': {
                        borderColor: index === 0 ? TEMPLATE_CORAL : TEMPLATE_PINK,
                        boxShadow: index === 0 ? '0 12px 28px rgba(246,65,131,0.42)' : '0 12px 28px rgba(0,0,0,0.26)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'inherit',
                        fontSize: { xs: '0.74rem', sm: '0.84rem', md: '0.9rem' },
                        lineHeight: 1.22,
                        fontWeight: 800,
                        textAlign: 'center',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <OpenInNew sx={{ color: 'inherit', fontSize: { xs: 15, sm: 17 }, flexShrink: 0 }} />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 2.4, md: 2.8 },
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: 'common.white', fontWeight: 900, fontSize: '1.05rem', mb: 0.75 }}>
            {t('contact.stillHaveQuestions')}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.95rem', lineHeight: 1.65 }}>
            {t('contact.stillHaveQuestionsBody')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Footer — navy bg anchor (reverse colors)
// ═══════════════════════════════════════════════════════════════════════
function FooterSection() {
  const { t } = useTranslation()
  const channels = t('contact.channels', { returnObjects: true }) as Array<{
    label: string
    value: string
    href?: string
    kind: 'email' | 'fanpage' | 'website' | 'phone'
  }>
  const emailChannel = channels.find((channel) => channel.kind === 'email')
  const quickLinks = [
    { label: t('nav.home'), href: '#hero' },
    { label: t('nav.about'), href: '#overview' },
    { label: t('nav.timeline'), href: '#timeline' },
    { label: t('nav.rules'), href: '#rules' },
    { label: t('nav.contact'), href: '#contact' },
  ]

  return (
    <Box
      sx={{
        py: { xs: 7, md: 9 },
        background: '#175892',
        color: 'common.white',
        borderTop: `1px solid rgba(255,151,151,0.28)`,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  sx={{
                    color: 'common.white',
                    fontWeight: 900,
                    fontSize: { xs: '1rem', md: '1.12rem' },
                    textTransform: 'uppercase',
                    letterSpacing: 4,
                    mb: 2.2,
                  }}
                >
                  {t('footer.aboutTitle')}
                </Typography>
                <Box sx={{ width: 58, height: 3, borderRadius: 999, background: TEMPLATE_CORAL }} />
              </Box>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.82)',
                  fontSize: { xs: '0.98rem', md: '1.05rem' },
                  lineHeight: 1.8,
                  maxWidth: 520,
                  fontWeight: 600,
                }}
              >
                {t('footer.projectDescription')}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src="/UEL_Logo final-02.png"
                  alt="UEL"
                  sx={{
                    width: { xs: 86, md: 108 },
                    height: { xs: 86, md: 108 },
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.16))',
                  }}
                />
                <Box
                  component="img"
                  src="/RLS_logo.png"
                  alt="RLS"
                  sx={{
                    width: { xs: 62, md: 82 },
                    height: { xs: 62, md: 82 },
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.16))',
                  }}
                />
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  sx={{
                    color: 'common.white',
                    fontWeight: 900,
                    fontSize: { xs: '1rem', md: '1.12rem' },
                    textTransform: 'uppercase',
                    letterSpacing: 4,
                    mb: 2.2,
                  }}
                >
                  {t('footer.quickLinksTitle')}
                </Typography>
                <Box sx={{ width: 58, height: 3, borderRadius: 999, background: TEMPLATE_CORAL }} />
              </Box>
              <Stack spacing={2.2}>
                {quickLinks.map((link) => (
                  <Typography
                    key={link.href}
                    component="a"
                    href={link.href}
                    sx={{
                      color: 'rgba(255,255,255,0.82)',
                      textDecoration: 'none',
                      fontSize: { xs: '1rem', md: '1.06rem' },
                      lineHeight: 1.4,
                      fontWeight: 600,
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: TEMPLATE_CORAL,
                      },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  sx={{
                    color: 'common.white',
                    fontWeight: 900,
                    fontSize: { xs: '1rem', md: '1.12rem' },
                    textTransform: 'uppercase',
                    letterSpacing: 4,
                    mb: 2.2,
                  }}
                >
                  {t('footer.contactTitle')}
                </Typography>
                <Box sx={{ width: 58, height: 3, borderRadius: 999, background: TEMPLATE_CORAL }} />
              </Box>
              <Stack spacing={1.7}>
                <Typography
                  sx={{
                    color: 'common.white',
                    fontWeight: 900,
                    fontSize: { xs: '1rem', md: '1.06rem' },
                    lineHeight: 1.5,
                  }}
                >
                  {t('footer.organizer')}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.82)',
                    fontSize: { xs: '1rem', md: '1.06rem' },
                    lineHeight: 1.75,
                    fontWeight: 600,
                  }}
                >
                  {t('footer.address')}
                </Typography>
              </Stack>
              {emailChannel ? (
                <Box sx={{ pt: 1 }}>
                  <Typography
                    sx={{
                      color: 'common.white',
                      fontWeight: 900,
                      fontSize: { xs: '1rem', md: '1.06rem' },
                      lineHeight: 1.5,
                      mb: 0.8,
                    }}
                  >
                    {t('footer.emailTitle')}
                  </Typography>
                  <Typography
                    component="a"
                    href={emailChannel.href || `mailto:${emailChannel.value}`}
                    sx={{
                      color: TEMPLATE_CORAL,
                      textDecoration: 'underline',
                      textDecorationColor: TEMPLATE_CORAL,
                      textUnderlineOffset: 5,
                      fontWeight: 900,
                      fontSize: { xs: '1rem', md: '1.06rem' },
                      wordBreak: 'break-word',
                    }}
                  >
                    {emailChannel.value}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Organizing Section — Ban Tổ chức & Hội đồng giám khảo
// ═══════════════════════════════════════════════════════════════════════
function OrganizingSection() {
  const { t } = useTranslation()
  const roles = t('organizing.committee.roles', { returnObjects: true }) as string[]
  const roleDisplayOrder = [0, 4, 1, 2, 3, 5]
  const orderedRoles = roleDisplayOrder.map((index) => roles[index]).filter(Boolean)
  const rounds = t('organizing.judges.rounds', { returnObjects: true }) as Array<{
    name: string
    body: string
  }>
  const [organizingRef, organizingEntered] = useOnceInView<HTMLDivElement>()

  return (
    <Box
      ref={organizingRef}
      id="organizers"
      sx={{
        py: { xs: 7, sm: 10 },
        background: BG_SOFT,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SectionWatermark number="05" side="left" />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="flex-start" textAlign="left" sx={{ mb: { xs: 3.5, md: 4.5 } }}>
          <Eyebrow>{t('organizing.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: INK,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '2rem', md: '2.95rem', lg: '3.25rem' },
              lineHeight: 1.06,
              maxWidth: 'none',
              whiteSpace: { md: 'nowrap' },
            }}
          >
            {t('organizing.title')}
          </Typography>
          <Box sx={{ width: 64, height: 3, background: gradientBrand, borderRadius: 1 }} />
          <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
            {t('organizing.subtitle')}
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.25, md: 2.65 },
                height: '100%',
                background: PINK_TINT_04,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${ACCENT_PINK}`,
                borderRadius: 2,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Stack spacing={{ xs: 1.7, md: 1.9 }}>
                <Stack direction="row" spacing={1.35} alignItems="center">
                  <Box
                    sx={{
                      width: { xs: 48, md: 52 },
                      height: { xs: 48, md: 52 },
                      borderRadius: '50%',
                      background: ACCENT_PINK,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Diversity3 sx={{ color: 'common.white', fontSize: 30 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minHeight: { xs: 48, md: 52 }, minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: INK, fontWeight: 800, fontSize: { xs: '1.08rem', sm: '1.18rem', md: '1.22rem' }, lineHeight: 1.16, textWrap: 'balance' }}
                    >
                      {t('organizing.committee.title')}
                    </Typography>
                  </Box>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.45, md: 1.6 },
                    borderRadius: 2,
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: PINK_TINT_10,
                        color: ACCENT_PINK,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.58, fontSize: '0.88rem' }}>
                      {t('organizing.committee.host')}
                    </Typography>
                  </Stack>
                </Paper>

                <Box>
                  <Eyebrow>{t('organizing.committee.rolesTitle')}</Eyebrow>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {orderedRoles.map((role, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 1.05, md: 1.1 },
                            height: '100%',
                            minHeight: { sm: 72, md: 76 },
                            borderRadius: 1.75,
                            background: BG_PRIMARY,
                            border: `1px solid ${i % 2 === 0 ? PINK_TINT_18 : BLUE_TINT_18}`,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Stack direction="row" spacing={0.85} alignItems="flex-start">
                            <CheckCircle
                              sx={{ color: i % 2 === 0 ? brand.hotPink : SOLID_BLUE, fontSize: 17, mt: 0.25, flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: TEXT_BODY,
                                lineHeight: 1.38,
                                fontSize: { xs: '0.82rem', md: '0.8rem' },
                                letterSpacing: -0.05,
                              }}
                            >
                              {role}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.25, md: 2.65 },
                height: '100%',
                background: BLUE_TINT_04,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${SOLID_BLUE}`,
                borderRadius: 2,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Stack spacing={{ xs: 1.7, md: 1.9 }}>
                <Stack direction="row" spacing={1.35} alignItems="center">
                  <Box
                    sx={{
                      width: { xs: 48, md: 52 },
                      height: { xs: 48, md: 52 },
                      borderRadius: '50%',
                      background: SOLID_BLUE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Gavel sx={{ color: 'common.white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minHeight: { xs: 48, md: 52 }, minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: INK, fontWeight: 800, fontSize: { xs: '1.08rem', sm: '1.18rem', md: '1.22rem' }, lineHeight: 1.16, textWrap: 'balance' }}
                    >
                      {t('organizing.judges.title')}
                    </Typography>
                  </Box>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.45, md: 1.6 },
                    borderRadius: 2,
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.58, fontSize: '0.88rem' }}>
                    {t('organizing.judges.intro')}
                  </Typography>
                </Paper>

                <Stack spacing={1.25}>
                  {rounds.map((round, i) => (
                    <Paper
                      key={i}
                      elevation={0}
                      sx={{
                        p: { xs: 1.25, md: 1.35 },
                        borderRadius: 1.75,
                        background: BG_PRIMARY,
                        border: `1px solid ${HAIRLINE}`,
                        borderLeft: `4px solid ${i % 2 === 0 ? SOLID_BLUE : ACCENT_PINK}`,
                      }}
                    >
                      <Chip
                        label={round.name}
                        size="small"
                        sx={{
                          background: i % 2 === 0 ? BLUE_TINT_08 : PINK_TINT_10,
                          color: i % 2 === 0 ? INK : ACCENT_PINK,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          mb: 1,
                          height: 22,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.5, fontSize: '0.84rem' }}>
                        {round.body}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 3, md: 3.5 },
            mx: 'auto',
            p: { xs: 2.2, md: 2.6 },
            maxWidth: 820,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF4F8 48%, #F2F7FE 100%)',
            border: `1.5px solid ${PINK_TINT_18}`,
            borderRadius: 3,
            boxShadow: '0 18px 42px rgba(20,78,140,0.12)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
            animation: organizingEntered ? 'pledgePulse 1650ms ease-out 250ms both' : 'none',
            '@keyframes pledgePulse': {
              '0%': { opacity: 0, transform: 'scale(0.96)' },
              '55%': { opacity: 1, transform: 'scale(1.02)' },
              '76%': { opacity: 1, transform: 'scale(0.992)' },
              '100%': { opacity: 1, transform: 'scale(1)' },
            },
            '&:hover': {
              borderColor: ACCENT_PINK,
              boxShadow: CARD_SHADOW_HOVER,
            },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.4, sm: 2 }}
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: { xs: 50, md: 56 },
                height: { xs: 50, md: 56 },
                borderRadius: '50%',
                background: ACCENT_PINK,
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WorkspacePremium sx={{ fontSize: { xs: 27, md: 30 } }} />
            </Box>
            <Stack spacing={0.65} alignItems={{ xs: 'center', sm: 'flex-start' }}>
              <Typography
                sx={{
                  color: ACCENT_PINK,
                  fontWeight: 900,
                  fontSize: { xs: '0.78rem', md: '0.86rem' },
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {t('organizing.pledgeTitle')}
              </Typography>
              <Typography
                sx={{
                  color: INK,
                  fontSize: { xs: '1.28rem', md: '1.62rem' },
                  fontWeight: 900,
                  lineHeight: 1.2,
                  textAlign: { xs: 'center', sm: 'left' },
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                {t('organizing.pledge').replace(/^"|"$/g, '')}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Media Section — thư viện khoảnh khắc
// ═══════════════════════════════════════════════════════════════════════
function MediaSection({ resultsVisible }: { resultsVisible: boolean }) {
  const { t } = useTranslation()
  const testimonials = t('media.testimonials', { returnObjects: true }) as Array<{
    quote: string
    author: string
  }>
  const activeTestimonial = 0
  const [mediaRef, mediaEntered] = useOnceInView<HTMLDivElement>()

  return (
    <Box
      ref={mediaRef}
      id="media"
      sx={{
        py: { xs: 8, md: 12 },
        background: BG_PINK,
        position: 'relative',
        overflow: 'hidden',
        ...sectionTexture,
      }}
    >
      <SectionWatermark
        number={resultsVisible ? '07' : '06'}
        side="center"
        top={resultsVisible ? { xs: -24, md: -48 } : { xs: -24 + WATERMARK_OFFSET_2CM, md: -48 + WATERMARK_OFFSET_2CM }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 3.5, md: 4.5 } }}>
          <Eyebrow>{t('media.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: INK,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '2.25rem', md: '3.25rem' },
              lineHeight: 1.06,
              maxWidth: 860,
            }}
          >
            {t('media.title')}
          </Typography>
          <Box sx={{ width: 64, height: 3, background: gradientBrand, borderRadius: 1 }} />
          <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
            {t('media.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={{
            maxWidth: 980,
            mx: 'auto',
            mb: { xs: 0, md: 0 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              aspectRatio: '16 / 9',
              minHeight: { xs: 260, sm: 380, md: 540 },
              borderRadius: 3,
              border: `1px solid ${PINK_TINT_18}`,
              background: BG_NAVY_DARK,
              color: 'common.white',
              boxShadow: CARD_SHADOW,
              position: 'relative',
              overflow: 'hidden',
              opacity: mediaEntered ? 1 : 0,
              animation: mediaEntered ? 'mediaFeatureIn 1450ms cubic-bezier(0.2, 0.8, 0.2, 1) 250ms both' : 'none',
              '@keyframes mediaFeatureIn': {
                '0%': { opacity: 0, transform: 'translateY(36px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
              transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
              '&:hover': {
                borderColor: TEMPLATE_PINK,
                boxShadow: CARD_SHADOW_HOVER,
              },
            }}
          >
            <Box
              component="iframe"
              src={RECAP_YOUTUBE_EMBED_URL}
              title={t('media.recapTitle')}
              allow="autoplay; encrypted-media"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 0,
                pointerEvents: 'none',
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                pointerEvents: 'none',
                background: 'linear-gradient(180deg, transparent 58%, rgba(7,27,51,0.54) 100%)',
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                cursor: 'default',
                background: 'transparent',
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                left: { xs: 20, md: 30 },
                bottom: { xs: 20, md: 30 },
                zIndex: 3,
                color: TEMPLATE_PINK,
                fontSize: { xs: '1.02rem', md: '1.46rem' },
                lineHeight: 1,
                letterSpacing: 1.8,
                textTransform: 'uppercase',
                fontWeight: 900,
                textShadow: '0 4px 18px rgba(0,0,0,0.45)',
              }}
            >
              {t('media.eyebrow')}
            </Typography>
          </Paper>
        </Box>

        {testimonials.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              mt: 5,
              p: { xs: 3, md: 3.5 },
              maxWidth: 760,
              mx: 'auto',
              border: `1px solid ${HAIRLINE}`,
              borderTop: `2px solid ${ACCENT_PINK}`,
              background: BG_PRIMARY,
              borderRadius: 2,
              boxShadow: CARD_SHADOW,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: INK, fontStyle: 'italic', lineHeight: 1.8, fontSize: '1rem' }}>
              &ldquo;{testimonials[activeTestimonial]?.quote}&rdquo;
            </Typography>
            <Typography
              sx={{
                color: TEXT_DIM,
                display: 'block',
                mt: 1.5,
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: 0.3,
              }}
            >
              — {testimonials[activeTestimonial]?.author}
            </Typography>
          </Paper>
        ) : null}
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// FAQ Section — accordion with search
// ═══════════════════════════════════════════════════════════════════════
function FAQSection({ resultsVisible }: { resultsVisible: boolean }) {
  const { t } = useTranslation()
  const topics = t('faq.topics', { returnObjects: true }) as string[]
  const items = t('faq.items', { returnObjects: true }) as Array<{
    question: string
    answer: string
  }>
  const [query, setQuery] = useState('')
  const normalizedQuery = normalizeSearchText(query)

  const filtered = useMemo(() => {
    if (!normalizedQuery) return items
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
    return items.filter((item) => {
      const haystack = `${normalizeSearchText(item.question)} ${normalizeSearchText(item.answer)}`
      return tokens.every((token) => haystack.includes(token))
    })
  }, [normalizedQuery, items])

  return (
    <Box id="faq" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY, position: 'relative', overflow: 'hidden' }}>
      <SectionWatermark
        number={resultsVisible ? '08' : '07'}
        side="center"
        top={resultsVisible ? { xs: 18 + WATERMARK_OFFSET_1CM, md: 24 + WATERMARK_OFFSET_1CM } : { xs: 18, md: 24 }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 3.5, md: 4.5 } }}>
          <Eyebrow>{t('faq.eyebrow')}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              color: INK,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: -0.5,
              fontSize: { xs: '2.2rem', md: '3.1rem' },
              lineHeight: 1.08,
              maxWidth: 840,
            }}
          >
            {t('faq.title')}
          </Typography>
          <Box sx={{ width: 64, height: 3, background: gradientBrand, borderRadius: 1 }} />
          <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
            {t('faq.subtitle')}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          {topics.map((topic) => (
            <Chip
              key={topic}
              label={topic}
              sx={{
                fontWeight: 700,
                color: INK,
                background: BG_PINK,
                border: `1px solid ${PINK_TINT_18}`,
              }}
            />
          ))}
        </Stack>

        <TextField
          variant="outlined"
          type="search"
          size="small"
          fullWidth
          placeholder={t('faq.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          sx={{
            display: 'block',
            maxWidth: 520,
            mx: 'auto',
            mb: 4,
            '& .MuiOutlinedInput-root': {
              background: BG_PRIMARY,
              borderRadius: 999,
              boxShadow: '0 8px 18px rgba(20,78,140,0.04)',
              '& fieldset': {
                borderColor: '#FFD6E4',
                borderWidth: 2,
              },
              '&:hover fieldset': {
                borderColor: TEMPLATE_PINK,
              },
              '&.Mui-focused fieldset': {
                borderColor: TEMPLATE_PINK,
                borderWidth: 2,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: SOLID_BLUE, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          {filtered.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('faq.emptyState')}
            </Alert>
          ) : (
            filtered.map((item, i) => (
              <Accordion
                key={i}
                disableGutters
                square={false}
                elevation={0}
                sx={{
                  mb: 1.5,
                  border: `1px solid #FFD6E4`,
                  borderRadius: 2,
                  background: BG_PRIMARY,
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  '&:hover': {
                    borderColor: TEMPLATE_PINK,
                  },
                  '&.Mui-expanded': {
                    borderColor: TEMPLATE_PINK,
                    boxShadow: '0 14px 34px rgba(246,65,131,0.13)',
                    background: BG_PRIMARY,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: INK }} />}
                  sx={{ px: 2.5, py: 0.5 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: INK, fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: TEXT_DIM, lineHeight: 1.7, whiteSpace: 'pre-line' }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Container>
    </Box>
  )
}

function FloatingLandingControls({ resultsVisible }: { resultsVisible: boolean }) {
  const { t } = useTranslation()
  const [activeSection, setActiveSection] = useState('hero')
  const registerHref =
    siteContent.registrationFormUrl ||
    'https://docs.google.com/forms/d/e/1FAIpQLSe7AXGzOp0rrxkAx1ipx3_fq0M0IFRj_i1BSClaQomLHPwlow/viewform'
  const sections = useMemo(
    () => [
      { id: 'hero', label: t('nav.home') },
      { id: 'overview', label: t('nav.about') },
      { id: 'timeline', label: t('nav.timeline') },
      { id: 'rules', label: t('nav.rules') },
      { id: 'awards', label: t('nav.awards') },
      { id: 'organizers', label: t('nav.organizers') },
      ...(resultsVisible ? [{ id: 'highlights', label: t('nav.results') }] : []),
      { id: 'media', label: t('media.title') },
      { id: 'faq', label: t('nav.faq') },
      { id: 'contact', label: t('nav.contact') },
    ],
    [resultsVisible, t],
  )

  useEffect(() => {
    const visible = new Map<string, number>()
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio)
          } else {
            visible.delete(entry.target.id)
          }
        })

        let nextActive = ''
        let maxRatio = 0
        visible.forEach((ratio, id) => {
          if (ratio > maxRatio) {
            maxRatio = ratio
            nextActive = id
          }
        })
        if (nextActive) {
          setActiveSection((prev) => (prev === nextActive ? prev : nextActive))
        }
      },
      {
        root: null,
        rootMargin: '-35% 0px -45% 0px',
        threshold: [0.08, 0.18, 0.32, 0.5, 0.68],
      },
    )

    const elements = sections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => Boolean(element))

    elements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [sections])

  return (
    <>
      <Box
        aria-label={t('nav.home')}
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 18 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1050,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 1.15,
          alignItems: 'center',
        }}
      >
        {sections.map((section) => {
          const active = activeSection === section.id

          return (
            <Tooltip key={section.id} title={section.label} placement="left" arrow>
              <Box
                component="a"
                href={`#${section.id}`}
                aria-label={section.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  textDecoration: 'none',
                  opacity: active ? 1 : 0.55,
                  transition: 'opacity 0.22s ease',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: active ? 12 : 8,
                    height: active ? 12 : 8,
                    borderRadius: '50%',
                    backgroundColor: active ? TEMPLATE_PINK : 'rgba(20,78,140,0.18)',
                    boxShadow: active ? '0 0 0 5px rgba(246,65,131,0.16)' : 'none',
                    transition: 'width 0.22s ease, height 0.22s ease, background-color 0.22s ease, box-shadow 0.22s ease',
                  }}
                />
              </Box>
            </Tooltip>
          )
        })}
      </Box>

      <Button
        component="a"
        href={registerHref}
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        startIcon={<HowToReg />}
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 24 },
          bottom: { xs: 16, md: 24 },
          zIndex: 1050,
          display: activeSection === 'timeline' ? 'none' : 'inline-flex',
          minHeight: { xs: 48, md: 54 },
          px: { xs: 2.2, sm: 3 },
          borderRadius: 999,
          background: TEMPLATE_PINK,
          color: 'common.white',
          fontWeight: 900,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          boxShadow: '0 18px 42px rgba(246,65,131,0.46)',
          border: '1px solid rgba(255,255,255,0.28)',
          '&:hover': {
            background: '#d62a6c',
            boxShadow: '0 20px 48px rgba(246,65,131,0.56)',
          },
        }}
      >
        {t('hero.ctaRegister')}
      </Button>
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════════════════════════
export default function PublicLandingPage() {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [scrolled, setScrolled] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const resultsVisible = now >= new Date(PRELIMINARY_RESULTS_VISIBLE_AT).getTime()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <Box sx={{ minHeight: '100dvh', background: BG_PRIMARY }}>
      <TopBar isAuthenticated={isAuthenticated} scrolled={scrolled} />
      <HeroSection isAuthenticated={isAuthenticated} />
      <OverviewSection />
      <ScheduleSection />
      <RulesSection />
      <AwardsSection />
      <OrganizingSection />
      {resultsVisible ? <ResultsSection /> : null}
      <MediaSection resultsVisible={resultsVisible} />
      <PartnersSection />
      <FAQSection resultsVisible={resultsVisible} />
      <ContactSection resultsVisible={resultsVisible} />
      <FooterSection />
      <FloatingLandingControls resultsVisible={resultsVisible} />
    </Box>
  )
}
